/* eslint-disable react/prop-types */
/**
 * BillingScreen — RestroPulse POS (Sprint 1)
 *
 * Three-column layout at 1366×768:
 *
 *   ┌─────────────────── top bar (thin, sticky) ────────────────────┐
 *   │  order-type   context (table/token/covers/steward/elapsed)     │
 *   │                                    outlet · user · connection  │
 *   ├─────────────────────┬───────────────────────────────────────────┤
 *   │   MENU (~55%)       │   ORDER TICKET (~45%)                     │
 *   │   search            │   scrolling line list                     │
 *   │   category strip    │                                           │
 *   │   item grid         │                                           │
 *   │   (scrolls)         │   totals block + action bar               │
 *   └─────────────────────┴───────────────────────────────────────────┘
 *
 * No page-level scroll. Only the grid and the line list scroll.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Search, X as XIcon, Wifi, WifiOff, Users, Clock, Plus, Minus,
  Trash2, MessageSquare, Percent, LayoutGrid, Printer, Send,
  Pause, Split, ArrowLeftRight, LogOut, Check, AlertTriangle, ChevronLeft,
} from "lucide-react";
import { ITEMS, CATEGORIES, STEWARDS, NOTE_PRESETS, TABLES } from "./mockData";
import { useCustomerBank, normalisePhone } from "./useCustomerBank";
import { ACT, ORDER_STATE, ORDER_TYPE, computeTotals, computeTotalsFor, hasUnsent, getActive, heldOrders, activeOrderForTable, childrenOf, outstandingChildrenOf, getActor, actorCan, staffNameFor, resolveTableLabel } from "./posReducer";
import axios from "axios";
import { toast as sonnerToast } from "sonner";
const POS_API = process.env.REACT_APP_BACKEND_URL;
import PaymentOverlay from "./PaymentOverlay";
import TillBadge from "./TillBadge";
import BillPreviewDialog from "./BillPreviewDialog";
import { DiscountDialog, SplitDialog, ManagerPinDialog, TransferDialog, OpenItemDialog } from "./Sprint2Dialogs";
import { ManagerPinDialog as SharedPinDialog } from "./Sprint4";
import QuantityElevationDialog from "./QuantityElevationDialog";
import { POS_CONFIG } from "./posConfig";
import { RotateCcw, Plus as PlusIcon } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import ReasonInput, { useReasonState, normaliseReason, reasonValid, VOID_PRESETS, CANCEL_PRESETS } from "./ReasonInput";

const fmt = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
});
const fmtInt = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", {
  minimumFractionDigits: 0, maximumFractionDigits: 0,
});

// ─── Sub-components ─────────────────────────────────────────────────
function OrderTypePicker({ value, onChange, disabled, hasLines, onBlockedClick }) {
  const opts = [
    { v: ORDER_TYPE.DINE_IN,      label: "Dine-in"      },
    { v: ORDER_TYPE.TAKEAWAY,     label: "Takeaway"     },
    { v: ORDER_TYPE.DELIVERY,     label: "Delivery"     },
    { v: ORDER_TYPE.ROOM_SERVICE, label: "Room service" },
  ];
  // Part 2 (Sprint 4) · switching type on an order that already has
  // lines is refused. A takeaway with a fired ticket cannot become
  // dine-in — the ticket is printed, the token issued, the kitchen
  // cooking. Only the currently-selected type stays clickable (as a
  // no-op) so the segmented control looks alive.
  const typeLocked = hasLines && !disabled;
  return (
    <div className="inline-flex shrink-0 rounded border border-[color:var(--pos-color-border,#E2E8DF)] bg-white overflow-hidden"
      data-testid="pos-order-type" role="tablist"
      data-type-locked={typeLocked ? "1" : "0"}
      title={typeLocked ? "Order type locked once lines exist. Return to the floor to start a different order." : undefined}>
      {opts.map((o) => {
        const sel = value === o.v;
        const isDisabled = disabled || (typeLocked && !sel);
        return (
          <button key={o.v} type="button" role="tab" aria-selected={sel}
            disabled={isDisabled}
            onClick={() => {
              if (isDisabled) { onBlockedClick && onBlockedClick(); return; }
              onChange(o.v);
            }}
            data-testid={`pos-order-type-${o.v.toLowerCase()}`}
            className={`min-h-[44px] px-3 text-[12px] font-medium transition disabled:opacity-40 disabled:cursor-not-allowed
              ${sel ? "bg-[color:var(--pos-color-selected)] text-[color:var(--pos-color-on-selected,#0B0F0E)] border-r border-[color:var(--pos-color-accent,var(--color-primary))]"
                    : "text-[color:var(--pos-color-primary-text,#1A2421)] hover:bg-[color:var(--pos-color-raised-surface,#F9F9F8)]"}`}>
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function ConnectionPill({ online, onToggle }) {
  return (
    <button type="button" onClick={onToggle}
      data-testid="pos-connection-pill"
      className={`inline-flex items-center gap-1.5 h-8 px-2.5 rounded-full text-[11px] font-medium
        ${online ? "bg-emerald-50 text-emerald-800 border border-emerald-200"
                 : "bg-rose-50 text-rose-800 border border-rose-200"}`}
      title="Click to toggle offline state">
      {online ? <Wifi className="w-3.5 h-3.5" /> : <WifiOff className="w-3.5 h-3.5" />}
      {online ? "Online" : "Offline"}
    </button>
  );
}

function Elapsed({ since }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(t);
  }, []);
  const mins = Math.max(0, Math.floor((now - since) / 60_000));
  return (
    <span className="inline-flex items-center gap-1 text-[11px] text-[color:var(--pos-color-secondary-text,#5C6B64)] tabular-nums"
      data-testid="pos-elapsed">
      <Clock className="w-3.5 h-3.5" /> {mins}m
    </span>
  );
}

// ─── Line action popover (Part 2 · shadcn portal) ──────────────────
function LineActionPopover({ line, open, onOpenChange, onOpenNote, onOpenLineDiscount, onRequestRemove, onRequestDec, dispatch, locked, orderState }) {
  if (!line) return null;
  const noteEditable = line.ticket_id == null && !locked;
  // Sprint 4 correction 2 · Remove and Decrease stay enabled on
  // PRINTED — the caller routes them through the reopen flow. Only
  // SETTLED / VOIDED are terminal.
  const editable = orderState !== ORDER_STATE.SETTLED
                && orderState !== ORDER_STATE.VOIDED;
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <span className="sr-only" aria-hidden="true" />
      </PopoverTrigger>
      <PopoverContent
        align="end" side="left" sideOffset={8} collisionPadding={8}
        className="w-56 p-0 border border-[color:var(--pos-color-border,#E2E8DF)] bg-white shadow-lg z-50"
        data-testid="pos-line-popover">
        <div className="px-3 py-2 border-b border-[color:var(--pos-color-border,#E2E8DF)]">
          <div className="text-[11px] text-[color:var(--pos-color-secondary-text,#5C6B64)]">Line action</div>
          <div className="text-[12px] font-medium text-[color:var(--pos-color-primary-text,#1A2421)] truncate">{line.item_name}</div>
        </div>
        <button type="button" disabled={locked}
          onClick={() => { dispatch({ type: ACT.LINE_QTY_CHANGED, payload: { line_id: line.id, qty: line.qty + 1 } }); onOpenChange(false); }}
          className="w-full text-left px-3 py-3 text-[13px] hover:bg-[color:var(--pos-color-raised-surface,#F9F9F8)] disabled:opacity-40 flex items-center gap-2 min-h-[44px]"
          data-testid="pos-line-popover-inc">
          <Plus className="w-3.5 h-3.5" /> Increase quantity
        </button>
        <button type="button" disabled={!editable || line.qty <= 1}
          onClick={() => { if (onRequestDec) onRequestDec(line); onOpenChange(false); }}
          className="w-full text-left px-3 py-3 text-[13px] hover:bg-[color:var(--pos-color-raised-surface,#F9F9F8)] disabled:opacity-40 flex items-center gap-2 min-h-[44px]"
          data-testid="pos-line-popover-dec">
          <Minus className="w-3.5 h-3.5" /> Decrease quantity
        </button>
        <button type="button" disabled={locked}
          onClick={() => { onOpenLineDiscount(); onOpenChange(false); }}
          className="w-full text-left px-3 py-3 text-[13px] hover:bg-[color:var(--pos-color-raised-surface,#F9F9F8)] disabled:opacity-40 flex items-center gap-2 min-h-[44px]"
          data-testid="pos-line-popover-discount">
          <Percent className="w-3.5 h-3.5" /> Add line discount
        </button>
        <button type="button"
          onClick={() => { if (onOpenNote) onOpenNote(); onOpenChange(false); }}
          className="w-full text-left px-3 py-3 text-[13px] hover:bg-[color:var(--pos-color-raised-surface,#F9F9F8)] flex items-center gap-2 min-h-[44px]"
          data-testid="pos-line-popover-note">
          <MessageSquare className="w-3.5 h-3.5" />
          {noteEditable ? "Kitchen / Bar note" : "View note"}
        </button>
        <button type="button" disabled={!editable}
          onClick={() => { onRequestRemove(line); onOpenChange(false); }}
          className="w-full text-left px-3 py-3 text-[13px] hover:bg-rose-50 text-rose-700 disabled:opacity-40 flex items-center gap-2 border-t border-[color:var(--pos-color-border,#E2E8DF)] min-h-[44px]"
          data-testid="pos-line-popover-void">
          <Trash2 className="w-3.5 h-3.5" /> Remove line
        </button>
      </PopoverContent>
    </Popover>
  );
}

// ─── Main component ─────────────────────────────────────────────────
export default function BillingScreen({ state, dispatch, livePos, onExit, onBack, onSettle, outletName, userName, onLock, onCloseTill }) {
  const active = getActive(state);
  const activeSafe = active || { order: {}, lines: [], payments: [] };
  const order = activeSafe.order;
  const lines = activeSafe.lines;
  const payments = activeSafe.payments;
  const connection = state.connection;
  const totals = useMemo(() => computeTotals(state), [state]);
  const unsent = hasUnsent(activeSafe);
  const held   = heldOrders(state);
  // "locked" means the ticket is read-only. PRINTED and VOIDED are
  // both locked-for-edit but with different reasons; SETTLED is
  // terminal.
  const locked = order.state === ORDER_STATE.PRINTED
              || order.state === ORDER_STATE.SETTLED
              || order.state === ORDER_STATE.VOIDED;
  const readOnly = order.state === ORDER_STATE.SETTLED
                || order.state === ORDER_STATE.VOIDED;

  // Sprint 2 correction A2 · settlement permission derives from the
  // CURRENTLY-logged-in staff, not the previous one. `getActor(state)`
  // resolves against `session.staff_snapshot`, which the STAFF_LOGGED_IN
  // handler swaps atomically on unlock. Client-side gate is UX only —
  // the server re-checks the token role on every /authorise-settlement
  // call, so a hidden button cannot be bypassed via a direct fetch.
  const canSettle = actorCan(state, "settle");
  // POS Control Sprint · once an order has been formally split, the
  // parent must not be accidentally printed or settled. Children run
  // their independent print/settle flows via SplitChildrenView; the
  // parent shell exists only to link them (Contract §6). We derive
  // the flag once here so print/settle/reprint gates all share it.
  const hasSplitChildren = Array.isArray(order.split_children) && order.split_children.length > 0;
  // Sprint 2 B.2 · till gate — settlement is refused when no till is
  // open for this cashier. The server /authorise-settlement checks the
  // same thing; this client gate keeps the button state honest so a
  // cashier can see WHY they cannot tap Settle right now.
  const hasOpenTill = !!state.session?.current_till;
  const tillReason = !canSettle
    ? `${getActor(state).role || "This role"} cannot settle. Ask a cashier or manager.`
    : (!hasOpenTill
        ? "Open your till before settling. Every settled tender is attributed to a till."
        : null);

  const [cat,      setCat]        = useState(CATEGORIES[0].id);
  const [q,        setQ]          = useState("");
  const [popover,  setPopover]    = useState(null);       // line id showing action popover
  const [printDlg, setPrintDlg]   = useState(false);      // 3-choice dialog when printing with unfired lines
  const [previewOpen, setPreviewOpen] = useState(false);  // bill preview dialog
  const [previewMode, setPreviewMode] = useState("print");// "print" or "reprint" (Part 8)
  const [splitOpen, setSplitOpen] = useState(false);      // Part 6 real split dialog
  const [splitViewOpen, setSplitViewOpen] = useState(false); // Part 3.2/3.3 · children overview
  const [splitViewParentId, setSplitViewParentId] = useState(null);
  const [childPayOrderId, setChildPayOrderId] = useState(null); // Part 3.3 · child under payment
  const [guestDlgOpen, setGuestDlgOpen] = useState(false);  // Part 5.1/5.2 · guest capture

  // Part 3 (Sprint 4) · Takeaway, Delivery and Room-service ALL open
  // the guest dialog on entry ONLY when the order is fresh (no lines
  // punched yet). Resuming an existing order — from the Unsettled
  // Orders Queue, a bill preview, or a lock-then-unlock cycle — must
  // NOT reprompt (Sprint 2 correction Part 2). The cashier can still
  // capture or edit guest details manually via the Guest button.
  useEffect(() => {
    if (readOnly) return;
    const isFresh = !order?.id || !(order?.lines_length_at_activation != null)
      ? (state?.orders?.[order?.id]?.lines?.length || 0) === 0
      : false;
    if (!isFresh) return;   // resume path — leave the operator alone
    if (order?.type === ORDER_TYPE.DELIVERY && !order.guest_address) { setGuestDlgOpen(true); return; }
    if ((order?.type === ORDER_TYPE.TAKEAWAY || order?.type === ORDER_TYPE.ROOM_SERVICE) && !order.guest_phone) {
      setGuestDlgOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order?.type, order?.id]);
  // POS Cleanup Pass 1 · 10-minute stale-empty auto-release.
  //   When a draft order sits at 0 lines / no invoice / no tenders for
  //   ≥10 minutes, we call the same `releaseDraft` action every other
  //   surface uses. The server repeats the eligibility check so the
  //   frontend timeout alone is not authority. Runs once per order.
  const staleReleaseRef = useRef(null);
  useEffect(() => {
    if (!order?.id) return;
    if (order.state !== ORDER_STATE.OPEN) return;
    if (lines.length !== 0) return;
    if (order.invoice_no) return;
    const openedAt = order.opened_at || 0;
    const ageMs = Date.now() - openedAt;
    const TEN_MIN = 10 * 60 * 1000;
    const trigger = async () => {
      if (staleReleaseRef.current === order.id) return;
      staleReleaseRef.current = order.id;
      if (livePos) {
        const r = await livePos.releaseDraft(order.id, "EMPTY_TIMEOUT");
        if (!r?.ok) return;
      } else {
        dispatch({ type: ACT.ORDER_DRAFT_RELEASED, payload: { order_id: order.id, reason: "EMPTY_TIMEOUT" }});
      }
      dispatch({ type: ACT.NAVIGATE_TO_FLOOR });
    };
    if (ageMs >= TEN_MIN) {
      trigger();
      return;
    }
    const t = setTimeout(trigger, TEN_MIN - ageMs);
    return () => clearTimeout(t);
  }, [order?.id, order?.state, order?.invoice_no, order?.opened_at, lines.length]); // eslint-disable-line react-hooks/exhaustive-deps

  //   When a hard refresh lands on a table that was previously split,
  //   the reducer activates a single order (parent or one child) —
  //   there's no "on-refresh" hook that opens the SplitChildrenView.
  //   This effect detects the two split shapes on the active order
  //   and auto-opens the consolidated view so the cashier sees ALL
  //   siblings + their SETTLED / PRINTED / OPEN states without
  //   re-triggering split.
  //   Runs once per order.id change; user can Close the view and the
  //   effect won't re-open until they navigate to a different order.
  const splitAutoOpenRef = useRef(null);
  useEffect(() => {
    if (!order?.id) return;
    if (splitAutoOpenRef.current === order.id) return;
    // Case A · active order is a split PARENT (has children).
    if (Array.isArray(order.split_children) && order.split_children.length > 0) {
      splitAutoOpenRef.current = order.id;
      setSplitViewParentId(order.id);
      setSplitViewOpen(true);
      return;
    }
    // Case B · active order is a split CHILD (has parent).
    if (order.split_from) {
      splitAutoOpenRef.current = order.id;
      setSplitViewParentId(order.split_from);
      setSplitViewOpen(true);
    }
  }, [order?.id, order?.split_from, order?.split_children]);

  const [pinOpen,   setPinOpen]   = useState(false);      // Part 6 manager PIN dialog (legacy post-print split)
  // POS Control Sprint · Reprint gating.
  //   Any reprint now captures a predefined reason + free-text notes
  //   BEFORE the Manager PIN prompt. When the reason is "OTHER" the
  //   notes are mandatory. The chosen reason/notes flow into the
  //   SharedPinDialog callback which routes to `livePos.reprintBill`
  //   with the audit payload.
  const [reprintReasonOpen, setReprintReasonOpen] = useState(false);
  // Part 5 (Sprint 4) · generic authorisation queue. When any gated
  // action fires, set `pinAction = { label, reason_prompt, run(auth) }`.
  // The SharedPinDialog reads this and calls run() on success.
  const [pinAction, setPinAction] = useState(null);
  const requestAuth = (label, run, reason_prompt) =>
    setPinAction({ label, reason_prompt, run });

  // ─── Sprint 8 · POS-QCS · quantity elevation dialog ────────────
  // When a cashier attempts to raise a line qty above NORMAL_MAX (999)
  // this state carries the pending elevation so we can render the
  // dialog once and route the approved authorisation_id back into
  // livePos.changeLineQty. Throttled so a stuck + key never opens the
  // dialog twice.
  const [qtyElevation, setQtyElevation] = useState(null);
  const lastQtyBlockToastRef = useRef(0);
  const attemptQtyChange = (line, nextQty) => {
    const currentQty = Number(line?.qty || 0);
    if (nextQty == null || nextQty <= 0) return;
    if (nextQty === currentQty) return;
    // Hard block above the manager ceiling — no dialog, only a toast.
    if (nextQty > 10_000) {
      const now = Date.now();
      if (now - lastQtyBlockToastRef.current > 1500) {
        lastQtyBlockToastRef.current = now;
        sonnerToast.error("Maximum allowed quantity is 10,000.");
      }
      return;
    }
    // Elevate — throttle so a stuck key doesn't reopen the dialog.
    if (nextQty > 999) {
      if (qtyElevation) return; // dialog already open
      const now = Date.now();
      if (now - lastQtyBlockToastRef.current < 500) return;
      lastQtyBlockToastRef.current = now;
      setQtyElevation({
        line_id:         line.id,
        intendedValue:   nextQty,
        previousValue:   currentQty,
        onApproved: (auth) => {
          setQtyElevation(null);
          if (livePos) {
            livePos.changeLineQty(order.id, line.id, nextQty, {
              authorisation_id: auth.authorisation_id,
            });
          } else {
            dispatch({ type: ACT.LINE_QTY_CHANGED, payload: { line_id: line.id, qty: nextQty } });
          }
        },
      });
      return;
    }
    // Normal path — dispatch directly.
    if (livePos) livePos.changeLineQty(order.id, line.id, nextQty);
    else dispatch({ type: ACT.LINE_QTY_CHANGED, payload: { line_id: line.id, qty: nextQty } });
  };

  // Sprint 4 correction 2 · Removing or reducing a line on a PRINTED
  // order goes through the SAME PIN → INVOICE_VOIDED → OPEN reopen
  // flow as "Add items". OPEN orders pass through directly. No
  // parallel mechanism — fiscal controls stay identical.
  const withReopenIfPrinted = (label, thenDispatch) => {
    if (order.state !== ORDER_STATE.PRINTED) { thenDispatch(); return; }
    requestAuth(
      // Financial Ground Truth (Feb 2026) · make the consequence
      // explicit to the manager approving the reopen: the current
      // invoice is voided, a new invoice number will be generated.
      `The current invoice ${order.invoice_no || order.id} will be voided and a new invoice number will be generated after items are edited. Proceed?`,
      async (auth) => {
        // Server-backed void when live; local reducer flip when demo.
        if (livePos) {
          const r = await livePos.voidInvoiceForReopen({
            order_id:               order.id,
            invoice_no:             order.invoice_no,
            reason:                 auth.reason_code || "REOPENED_FOR_LINE_EDIT",
            authorised_by_staff_id: auth.authorised_by_id || auth.authorised_by,
          });
          if (!r?.ok) return; // toast already shown by thunk
        } else {
          dispatch({ type: ACT.INVOICE_VOIDED, payload: {
            source_order_id: order.id,
            voided_invoice:  order.invoice_no,
            authorised_by:   auth.authorised_by,
            at:              auth.at,
            reason_code:     "REOPENED_FOR_LINE_EDIT",
            note:            auth.reason_code || null,
          }});
        }
        thenDispatch();
      },
      "Reason (optional)",
    );
  };
  const [transferOpen, setTransferOpen] = useState(false);// Part 7 transfer dialog
  const [discountOpen, setDiscountOpen] = useState(false);// Part 5 discount dialog
  const [lineDiscountFor, setLineDiscountFor] = useState(null); // Part 3 · line-scoped discount
  const [openItemOpen, setOpenItemOpen] = useState(false);// Part 8 open item dialog
  const [voidDlg,  setVoidDlg]    = useState(false);      // void confirmation dialog
  const [paymentOpen, setPaymentOpen] = useState(false);  // settle overlay
  const [noteDlgLine, setNoteDlgLine] = useState(null);   // Task 1c — kitchen note dialog target line
  // Sprint 2 Correction Part 1 · confirm-before-fire dialog. The
  // ticket must never fire until the cashier acknowledges the list.
  // The KOT preview slip that used to appear AFTER firing is gone —
  // it implied approval of an irreversible action already taken.
  const [kotConfirmOpen, setKotConfirmOpen] = useState(false);
  const [heldTrayOpen, setHeldTrayOpen] = useState(false);// Task 2 — held-orders tray
  const [cancelSlip, setCancelSlip] = useState(null);     // most-recent TICKET_ITEM_CANCELLED slip
  const [cancelReasonLine, setCancelReasonLine] = useState(null); // Part 5 · cancel-reason dialog before qty dec on fired line
  const cancelCountRef = useRef((state.session.ticket_cancellations || []).length);
  const searchRef = useRef(null);
  // POS UX closure (Feb 2026) · scroll container for the order-ticket
  // panel. Attached to the existing `pos-line-list` div; used only by
  // the "newest line remains visible" auto-scroll effect below.
  const linesListRef = useRef(null);
  const prevLinesCountRef = useRef(lines.length);

  // POS UX closure (Feb 2026) · when a NEW line is punched onto the
  // ticket, scroll the order-ticket container to the bottom so the
  // freshly added row remains visible. Only fires when `lines.length`
  // GROWS — void (decrease) and pure quantity edits (no length change,
  // no effect run at all) leave the scroll alone. The prior-count ref
  // is tracked separately from React state so this effect never fires
  // on unrelated re-renders. Also — smooth scrolling on the existing
  // `pos-line-list` container only; the surrounding page never moves.
  useEffect(() => {
    if (lines.length > prevLinesCountRef.current && linesListRef.current) {
      const el = linesListRef.current;
      // Use requestAnimationFrame so we scroll AFTER the new row has
      // laid out — otherwise scrollHeight can still reflect the old
      // list and the newest row stays a hair below the fold.
      requestAnimationFrame(() => {
        try { el.scrollTo({ top: el.scrollHeight, behavior: "smooth" }); }
        catch (_) { el.scrollTop = el.scrollHeight; }
      });
    }
    prevLinesCountRef.current = lines.length;
  }, [lines.length]);

  // Task 1c — search is available IMMEDIATELY on arrival at the
  // billing screen with no prior tap. Refocus whenever the active
  // order changes (e.g. new order via New takeaway, or re-entering a
  // table from the floor view).
  useEffect(() => {
    if (!locked) searchRef.current?.focus();
  }, [order.id, locked]);

  // Watch for new TICKET_ITEM_CANCELLED events and surface the last one
  // as a dismissable cancellation-slip dialog (mock — real cashier
  // would see a printed slip come off the ticket printer).
  useEffect(() => {
    const cancellations = state.session.ticket_cancellations || [];
    const n = cancellations.length;
    if (n > cancelCountRef.current) {
      setCancelSlip(cancellations[n - 1]);
    }
    cancelCountRef.current = n;
  }, [state.session.ticket_cancellations]);

  // Ctrl+F focuses search. Escape returns to floor when nothing else
  // is open — Task 1e leaves the order intact.
  useEffect(() => {
    const h = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "f") {
        e.preventDefault(); searchRef.current?.focus(); return;
      }
      if (e.key === "Escape") {
        if (printDlg || voidDlg || paymentOpen || noteDlgLine || cancelSlip || heldTrayOpen) return;
        if (onBack) { e.preventDefault(); onBack(); }
      }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [printDlg, voidDlg, paymentOpen, noteDlgLine, cancelSlip, heldTrayOpen, onBack]);

  const filteredItems = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (term) {
      return ITEMS.filter((it) => it.name.toLowerCase().includes(term)).slice(0, 60);
    }
    if (cat === "cat_open_items") {
      // Sprint 4 correction 4 · managed open items (business-day scoped).
      return (state.session.managed_open_items || []).map((o) => ({
        id: o.id, name: o.name, price: o.price,
        category_id: "cat_open_items", _open_item: o,
      }));
    }
    return ITEMS.filter((it) => it.category_id === cat);
  }, [q, cat, state.session.managed_open_items]);

  // Sprint 2 correction Part 3 · resolve steward name via server-cached
  // snapshot first, then via the reducer helper. Never dump the raw ID.
  const stewardName = order.steward_name
    || staffNameFor(state, order.steward_id)
    || (STEWARDS.find((s) => s.id === order.steward_id)?.name)
    || "—";

  // Part 4 — 1-click add. No modifier popup (Part 6.1 removes variants
  // entirely). Every sellable line is a distinct menu item now.
  // Part 4 (Sprint 3) · a dine-in order refuses to accept items with
  // no table selected; likewise room service needs a room number.
  const [needsTableToast, setNeedsTableToast] = useState(false);
  const needsTable = (order.type === "DINE_IN" || order.type === "ROOM_SERVICE") && !order.table_id;
  // POS UX closure (Feb 2026) · after a successful punch, wipe the
  // search field and drop focus back into it so the cashier can type
  // the next item without a reach for the mouse. Applies to BOTH the
  // menu-tile tap path AND the managed-open-item punch path. Runs
  // ONLY on success — a locked order, a missing table, a validation
  // block or a server rejection leaves the query intact.
  const clearSearchAndRefocus = () => {
    setQ("");
    // Refocus after the state update flushes so the caret lands in
    // the (now empty) input rather than the previous DOM snapshot.
    setTimeout(() => { searchRef.current?.focus(); }, 0);
  };
  const onTileTap = (it) => {
    if (locked) return;
    if (needsTable) { setNeedsTableToast(true); setTimeout(() => setNeedsTableToast(false), 3000); return; }
    if (it._open_item) {
      // Sprint 4 correction 4 · managed open item — punch as an open
      // line so tax + routing follow the entry's category.
      dispatch({ type: ACT.LINE_ADDED_OPEN, payload: {
        category: it._open_item.category,
        name:     it._open_item.name,
        price:    it._open_item.price,
        qty:      1,
        punched_by: userName || "system",
      }});
      // Managed open items dispatch synchronously; the reducer never
      // rejects a well-formed payload so this is treated as a success.
      clearSearchAndRefocus();
      return;
    }
    // Sprint 2 correction A3 · capture the item as a FROZEN snapshot at
    // the moment of the tap. Rapid taps against different tiles used to
    // occasionally converge on a single item because the reducer /
    // thunk closure was reading a value that had drifted between the
    // click and the async dispatch. Snapshot the primitives here and
    // pass them explicitly so nothing downstream can resolve a stale
    // item. Every field the reducer or the API needs is on this object.
    const snap = Object.freeze({
      id:      it.id,
      name:    it.name,
      price:   Number(it.price) || 0,
      routing: it.routing || "kitchen",
      note:    "",
    });
    if (livePos) {
      livePos.addLine(order.id, snap).then((r) => {
        if (r && r.ok) clearSearchAndRefocus();
      });
    } else {
      dispatch({ type: ACT.LINE_ADDED, payload: {
        item_id: snap.id, name: snap.name, price: snap.price, routing: snap.routing,
      }});
      // Demo / offline reducer path — the reducer accepts unconditionally.
      clearSearchAndRefocus();
    }
  };

  // Part 4 — Repeat button. Adds a fresh copy of the LAST line on the
  // ticket. If a fired identical line exists but no unfired match, a
  // new unfired line is appended (Contract §4 append-only).
  const onRepeat = () => {
    if (locked || lines.length === 0) return;
    const last = lines[lines.length - 1];
    const asItem = { id: last.item_id, name: last.item_name, price: last.unit_price, routing: last.routing, note: last.note };
    if (livePos) livePos.addLine(order.id, asItem);
    else dispatch({ type: ACT.LINE_ADDED, payload: { item_id: last.item_id, note: last.note } });
  };

  // ─── UI ─────────────────────────────────────────────────────────
  return (
    <div className="h-full w-full bg-[color:var(--pos-color-page-bg,#F4F6F2)] flex flex-col overflow-hidden"
      data-testid="pos-billing-screen">

      {/* Offline banner — full-width, persistent, unmissable but non-blocking */}
      {!connection.online && (
        <div className="bg-rose-800 text-white px-4 py-2 flex items-center justify-between gap-3"
          data-testid="pos-offline-banner">
          <div className="flex items-center gap-2 text-[12px] font-medium">
            <WifiOff className="w-4 h-4" />
            Billing continuing offline — orders will sync when the connection returns.
          </div>
          <span className="text-[10px] uppercase tracking-wider bg-white/15 rounded px-1.5 py-0.5">Local mode</span>
        </div>
      )}

      {/* Part 4 (Sprint 3) · dine-in without a table cannot accept items */}
      {needsTable && (
        <div className="bg-amber-100 border-b border-amber-300 text-amber-900 px-4 py-2 text-[12px]"
          data-testid="pos-needs-table-banner">
          {order.type === "DINE_IN"
            ? <>This dine-in order has no table assigned. Return to the floor and select a table before adding items.</>
            : <>This room-service order has no room number. Set a room number before adding items.</>
          }
        </div>
      )}
      {needsTableToast && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 bg-amber-700 text-white px-4 py-2 rounded shadow-lg z-[70] text-[12px]"
          data-testid="pos-needs-table-toast">
          {order.type === "DINE_IN"
            ? "Select a table before adding items."
            : "Set a room number before adding items."
          }
        </div>
      )}

      {/* Top bar */}
      <div className="border-b border-[color:var(--pos-color-border,#E2E8DF)] bg-white px-3 py-2 flex items-center justify-between gap-3 shrink-0"
        data-testid="pos-top-bar">
        <div className="flex items-center gap-2 min-w-0">
          {onBack && (
            <button type="button" onClick={onBack}
              className="inline-flex items-center gap-1 h-8 px-2 rounded border border-[color:var(--pos-color-border,#E2E8DF)] text-[11px] text-[color:var(--pos-color-secondary-text,#5C6B64)] hover:text-[color:var(--pos-color-primary-text,#1A2421)] hover:bg-[color:var(--pos-color-raised-surface,#F9F9F8)]"
              data-testid="pos-back-to-floor" aria-label="Back to floor · Esc">
              <ChevronLeft className="w-3.5 h-3.5" /> Floor
            </button>
          )}
          <OrderTypePicker value={order.type} disabled={readOnly}
            hasLines={(active.lines || []).length > 0}
            onChange={(v) => {
              // Part 2 (Sprint 4) · switching TO dine-in with no table
              // yet — take the operator to the floor directly (they
              // clearly intend to pick a table). We back out to the
              // floor and drop the current empty order.
              if (v === ORDER_TYPE.DINE_IN && !order.table_id && (active.lines || []).length === 0) {
                onBack && onBack();
                return;
              }
              dispatch({ type: ACT.ORDER_TYPE_SET, payload: { type: v } });
            }} />

          {/* Order type-dependent context — DIFFERENT SHAPE per type
              Fix (Feb 2026) · `min-w-0` + `flex-1` lets this row shrink
              when Delivery renders a wider "address needed" pill, so
              Room service never gets pushed off the top strip. */}
          <div className="flex items-center gap-2 pl-2 border-l border-[color:var(--pos-color-border,#E2E8DF)] min-w-0 flex-1 overflow-hidden" data-testid="pos-order-context">
            {order.type === ORDER_TYPE.DINE_IN && (
              <>
                <span className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded border border-[color:var(--pos-color-border,#E2E8DF)] bg-[color:var(--pos-color-raised-surface,#F9F9F8)]">
                  <LayoutGrid className="w-3.5 h-3.5 text-[color:var(--pos-color-secondary-text,#5C6B64)]" />
                  <span className="text-[12px] font-medium text-[color:var(--pos-color-primary-text,#1A2421)]" data-testid="pos-ctx-table">
                    {TABLES.find((t) => t.id === order.table_id)?.label || "—"}
                  </span>
                </span>
                <span className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded border border-[color:var(--pos-color-border,#E2E8DF)] bg-[color:var(--pos-color-raised-surface,#F9F9F8)]">
                  <Users className="w-3.5 h-3.5 text-[color:var(--pos-color-secondary-text,#5C6B64)]" />
                  <span className="text-[12px] font-medium text-[color:var(--pos-color-primary-text,#1A2421)] tabular-nums" data-testid="pos-ctx-covers">
                    {order.covers != null ? `${order.covers} cov` : "—"}
                  </span>
                </span>
                {order.guest_name && (
                  <span className="text-[11px] text-[color:var(--pos-color-secondary-text,#5C6B64)]" data-testid="pos-ctx-guest">
                    Guest: <strong className="text-[color:var(--pos-color-primary-text,#1A2421)]">{order.guest_name}</strong>
                  </span>
                )}
              </>
            )}
            {order.type === ORDER_TYPE.TAKEAWAY && (
              <button type="button" onClick={() => setGuestDlgOpen(true)}
                data-testid="pos-ctx-guest-open"
                className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded border border-amber-200 bg-amber-50 hover:bg-amber-100 transition">
                <span className="text-[10px] uppercase tracking-wider text-amber-800">Token</span>
                <span className="text-[13px] font-semibold text-[color:var(--pos-color-primary-text,#1A2421)] tabular-nums" data-testid="pos-ctx-token">{order.token || "—"}</span>
                {order.guest_name && <span className="text-[10px] text-amber-900 ml-1">· {order.guest_name}</span>}
              </button>
            )}
            {order.type === ORDER_TYPE.DELIVERY && (
              <button type="button" onClick={() => setGuestDlgOpen(true)}
                data-testid="pos-ctx-guest-open"
                className={`inline-flex items-center gap-1.5 h-8 px-2.5 min-w-0 max-w-full rounded border transition
                  ${order.guest_address
                    ? "border-sky-200 bg-sky-50 hover:bg-sky-100"
                    : "border-rose-300 bg-rose-50 hover:bg-rose-100"}`}>
                {/* Fix (Feb 2026) · single-line, vertically centred,
                    truncated only when the row genuinely runs out of
                    width. The pill keeps its 32-px height. */}
                <span className={`text-[10px] uppercase tracking-wider leading-none whitespace-nowrap truncate
                  ${order.guest_address ? "text-sky-800" : "text-rose-800"}`}>
                  {order.guest_address ? "Delivery" : "Delivery · address needed"}
                </span>
                <span className="text-[13px] font-semibold text-[color:var(--pos-color-primary-text,#1A2421)] tabular-nums shrink-0" data-testid="pos-ctx-token">{order.token || "—"}</span>
              </button>
            )}
            {order.type === ORDER_TYPE.ROOM_SERVICE && (
              <button type="button" onClick={() => setGuestDlgOpen(true)}
                data-testid="pos-ctx-guest-open"
                className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded border border-purple-200 bg-purple-50 hover:bg-purple-100 transition">
                <span className="text-[10px] uppercase tracking-wider text-purple-800">Room</span>
                <span className="text-[13px] font-semibold text-[color:var(--pos-color-primary-text,#1A2421)] tabular-nums" data-testid="pos-ctx-table">{order.table_id || "—"}</span>
                {order.guest_name && <span className="text-[10px] text-purple-900 ml-1">· {order.guest_name}</span>}
              </button>
            )}
            <span className="text-[11px] text-[color:var(--pos-color-secondary-text,#5C6B64)]" data-testid="pos-ctx-steward">Steward: <strong className="text-[color:var(--pos-color-primary-text,#1A2421)]">{stewardName}</strong></span>
            <Elapsed since={order.opened_at} />
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <span className="text-[11px] text-[color:var(--pos-color-secondary-text,#5C6B64)]">Outlet: <strong className="text-[color:var(--pos-color-primary-text,#1A2421)]">{outletName}</strong></span>
          <span className="text-[11px] text-[color:var(--pos-color-secondary-text,#5C6B64)]">User: <strong className="text-[color:var(--pos-color-primary-text,#1A2421)]">{userName}</strong></span>
          {/* Sprint 2 B.2 · shared TillBadge — same source-of-truth as
              FloorView. Amber-nudges after tenant threshold. */}
          <TillBadge state={state} userName={userName} theme="light" />
          <ConnectionPill online={connection.online}
            onToggle={() => dispatch({ type: ACT.CONNECTION_TOGGLED })} />
          {/* ITER-cx1 · Exit affordance — small, secondary, far from
              Send KOT / Print / Settle (which live at the bottom of
              the right column). Deliberately understated so a cashier
              can't hit it by accident during service. Two-step confirm
              on top of the small size for a belt-and-braces exit. */}
          {onExit && (
            <button
              type="button"
              onClick={() => {
                if (window.confirm("Exit terminal? Any unsettled work will be lost.")) {
                  onExit();
                }
              }}
              className="inline-flex items-center gap-1.5 h-11 min-w-[44px] px-3 rounded border border-[color:var(--pos-color-border,#E2E8DF)] text-[12px] text-[color:var(--pos-color-secondary-text,#5C6B64)] hover:text-[color:var(--pos-color-primary-text,#1A2421)] hover:bg-[color:var(--pos-color-raised-surface,#F9F9F8)] transition"
              data-testid="pos-exit-terminal"
              aria-label="Exit terminal">
              <LogOut className="w-4 h-4" /> Exit terminal
            </button>
          )}
          {/* Held-orders tray access — count badge visible even from
              billing so the cashier is aware of parked tickets. */}
          <button type="button" onClick={() => setHeldTrayOpen(true)}
            data-testid="pos-header-held-tray"
            className="inline-flex items-center gap-1.5 h-11 min-w-[44px] px-3 rounded border border-[color:var(--pos-color-border,#E2E8DF)] text-[12px] text-[color:var(--pos-color-secondary-text,#5C6B64)] hover:text-[color:var(--pos-color-primary-text,#1A2421)] hover:bg-[color:var(--pos-color-raised-surface,#F9F9F8)] transition relative">
            <Pause className="w-4 h-4" /> Held
            {held.length > 0 && (
              <span className="ml-0.5 inline-flex items-center justify-center min-w-[16px] h-[14px] px-1 rounded-full bg-sky-500 text-white text-[9px] font-semibold tabular-nums"
                data-testid="pos-header-held-count">{held.length}</span>
            )}
          </button>
          {/* Part 3.2/3.3 · Split view access. Shown whenever the
              current order has non-terminal children so the cashier
              can never lose sight of an unsettled sibling. */}
          {outstandingChildrenOf(state, order.id).length > 0 && (
            <button type="button"
              onClick={() => { setSplitViewParentId(order.id); setSplitViewOpen(true); }}
              data-testid="pos-header-split-view"
              className="inline-flex items-center gap-1.5 h-11 min-w-[44px] px-3 rounded border border-purple-300 text-[12px] text-purple-700 hover:text-purple-900 hover:bg-purple-50 transition relative">
              <Split className="w-4 h-4" /> Split view
              <span className="ml-0.5 inline-flex items-center justify-center min-w-[16px] h-[14px] px-1 rounded-full bg-purple-500 text-white text-[9px] font-semibold tabular-nums"
                data-testid="pos-header-split-count">
                {outstandingChildrenOf(state, order.id).length}
              </span>
            </button>
          )}
        </div>
      </div>

      {/* Body — TICKET LEFT · MENU RIGHT (swapped per Sprint 2 correction).
          Proportions and internal layouts unchanged; only positional. */}
      <div className="flex-1 flex overflow-hidden">

        {/* ─── LEFT: order ticket ─────────────────────────────────
            LAYOUT RULE (see Sprint 4 correction 1c) — this panel's
            width is FIXED. It carries `min-w-[480px]` as a hard floor
            so name + qty stepper + rate + amount always render on a
            single row without wrapping. `w-[45%]` still governs the
            preferred proportion on wider screens. Do NOT modify these
            values in any future sprint unless a correction names this
            panel explicitly. Adding categories on the right must NOT
            change anything in this panel — the right pane owns the
            overflow with its own horizontal scroll on the category
            strip.
        ─────────────────────────────────────────────────────────── */}
        <div className="w-[45%] min-w-[480px] shrink-0 flex flex-col bg-white border-r border-[color:var(--pos-color-border,#E2E8DF)] overflow-hidden">
          <div className="border-b border-[color:var(--pos-color-border,#E2E8DF)] px-3 py-1.5 flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[color:var(--pos-color-secondary-text,#5C6B64)]">Order Ticket</div>
              <div className="text-[13px] font-semibold text-[color:var(--pos-color-primary-text,#1A2421)] tabular-nums" data-testid="pos-ticket-count">
                {lines.length} item{lines.length !== 1 ? "s" : ""}
                {unsent && (
                  <span className="ml-2 text-[10px] uppercase tracking-wider bg-amber-100 text-amber-900 border border-amber-200 rounded px-1.5 py-[1px]"
                    data-testid="pos-unsent-badge">
                    Unsent
                  </span>
                )}
                {order.invoice_no && (
                  <span className="ml-2 text-[10px] uppercase tracking-wider bg-purple-100 text-purple-900 border border-purple-200 rounded px-1.5 py-[1px] tabular-nums"
                    data-testid="pos-invoice-no">
                    {order.invoice_no}
                  </span>
                )}
              </div>
            </div>
            <div>
              <span className={`text-[10px] uppercase tracking-wider rounded px-1.5 py-[1px] border
                ${order.state === ORDER_STATE.OPEN    ? "bg-amber-50 text-amber-800 border-amber-200"
                : order.state === ORDER_STATE.PRINTED ? "bg-purple-50 text-purple-800 border-purple-200"
                : order.state === ORDER_STATE.HELD    ? "bg-sky-50    text-sky-800    border-sky-200"
                : order.state === ORDER_STATE.VOIDED  ? "bg-rose-50   text-rose-800   border-rose-200"
                :                                       "bg-emerald-50 text-emerald-800 border-emerald-200"}`}
                data-testid="pos-order-state">
                {order.state}
              </span>
            </div>
          </div>

          {/* Sprint 2 B.2 · inline rollback banner. Persists on the
              order until the cashier dismisses it or the next action
              clears the marker via ORDER_SYNCED_FROM_SERVER. */}
          {order._error && (
            <div className="mx-3 mt-2 rounded border border-rose-300 bg-rose-50 px-3 py-2 flex items-start gap-2"
              data-testid="pos-order-inline-error">
              <AlertTriangle className="w-4 h-4 text-rose-700 shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="text-[11px] uppercase tracking-wider text-rose-800 font-semibold">
                  Change rolled back
                </div>
                <div className="text-[12px] text-rose-900 leading-snug">{order._error.reason}</div>
              </div>
              <button type="button"
                onClick={() => livePos?.clearOrderError?.(order.id)}
                className="text-rose-700 hover:text-rose-900 shrink-0"
                data-testid="pos-order-inline-error-dismiss"
                aria-label="Dismiss">
                <XIcon className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Line list — the ONLY scrolling region on the left */}
          <div ref={linesListRef} className="flex-1 overflow-y-auto" data-testid="pos-line-list">
            {lines.length === 0 && (
              <div className="h-full flex flex-col items-center justify-center px-8 text-center gap-3"
                data-testid="pos-empty-order">
                <div className="w-14 h-14 rounded-full border-2 border-dashed border-[#C6D0CC] flex items-center justify-center text-[#B8B8B5]">
                  <Plus className="w-6 h-6" />
                </div>
                <div>
                  <div className="text-[13px] font-medium text-[color:var(--pos-color-primary-text,#1A2421)]">Empty order</div>
                  <p className="text-[11px] text-[color:var(--pos-color-secondary-text,#5C6B64)] mt-1 max-w-[240px]">
                    Tap any item on the right to start. Speed is the point —
                    no confirmation dialogs, no in-between screens.
                  </p>
                </div>
              </div>
            )}

            {/* Task 1d — lines grouped by ticket batch (firing order), with
                a final "Not sent yet" group at the bottom for unfired
                lines. Group headers are one line high so the ticket
                reads fast during service. */}
            {groupByTicket(lines).map((group, gi) => (
              <div key={group.ticket_id || "__unsent"} data-testid={`pos-ticket-group-${group.ticket_id || "unsent"}`}>
                <TicketGroupHeader group={group} />
                {group.lines.map((line) => {
                  const globalIdx = lines.findIndex((l) => l.id === line.id);
                  return (
                    <LineRow key={line.id}
                      line={line} idx={globalIdx}
                      popoverOpen={popover === line.id}
                      onOpenPopover={() => setPopover(line.id)}
                      onClosePopover={() => setPopover(null)}
                      onOpenNote={() => { setNoteDlgLine(line); setPopover(null); }}
                      onOpenLineDiscount={() => { setLineDiscountFor(line); setPopover(null); }}
                      onOpenCancelReason={(l) => { setCancelReasonLine(l); setPopover(null); }}
                      onQtyRequest={attemptQtyChange}
                      onRequestUnfiredDec={(l) => {
                        // Unfired lines cannot exist on a PRINTED bill
                        // (BILL_PRINTED refuses them), so PRINTED here
                        // is a defensive branch only.
                        withReopenIfPrinted(
                          `Decrease unfired line · ${l.item_name}`,
                          () => {
                            if (livePos) livePos.changeLineQty(order.id, l.id, l.qty - 1);
                            else dispatch({ type: ACT.LINE_QTY_CHANGED, payload: { line_id: l.id, qty: l.qty - 1 } });
                          },
                        );
                      }}
                      onRequestRemove={(l) => {
                        // Sprint 4 correction 2 · Fired lines carry a
                        // reason (kitchen already cooked it). Unfired
                        // lines don't. On PRINTED, both flow through
                        // the SAME INVOICE_VOIDED reopen mechanism as
                        // "Add items".
                        if (l.ticket_id) {
                          setCancelReasonLine({ ...l, _remove_all: true });
                        } else {
                          withReopenIfPrinted(
                            `Remove unfired line · ${l.item_name}`,
                            () => {
                              if (livePos) livePos.voidLine(order.id, l.id);
                              else dispatch({ type: ACT.LINE_VOIDED, payload: { line_id: l.id } });
                            },
                          );
                        }
                      }}
                      dispatch={dispatch} livePos={livePos} orderId={order.id} locked={locked} orderState={order.state} />
                  );
                })}
              </div>
            ))}
          </div>

          {/* Totals block */}
          <TotalsBlock totals={totals} order={order} />

          {/* Action bar — tighter to give the line list more room.
              Buttons keep the 44px min touch target, only container
              chrome shrinks. Two rows of five in a compact grid. */}
          <div className="border-t border-[color:var(--pos-color-border,#E2E8DF)] px-2 py-1 flex flex-wrap gap-1" data-testid="pos-action-bar">
            {/* Item 5 (Sprint 4 corrections) · flex-wrap layout so
                buttons widen to fit their label; nothing truncates at
                1366×768. Buttons may sit on more than one row. */}
            <ActionBtn testid="pos-action-kot" icon={Send} label="Send ticket"
              onClick={() => setKotConfirmOpen(true)}
              disabled={locked || !unsent} primary={unsent && !locked} />
            <ActionBtn testid="pos-action-print" icon={Printer} label="Print bill"
              onClick={() => {
                if (unsent) { setPrintDlg(true); return; }
                setPreviewMode("print");
                setPreviewOpen(true);
              }}
              disabled={locked || lines.length === 0 || hasSplitChildren}
              disabledReason={hasSplitChildren ? "Order was split — print each child bill from the Split view." : null}
              primary={!locked && lines.length > 0 && !unsent && !hasSplitChildren} />
            <ActionBtn testid="pos-action-settle" icon={Check} label="Settle"
              onClick={async () => {
                if (!canSettle || !hasOpenTill) return;
                // Sprint 2 correction A2 · server-authoritative gate.
                // The reducer's mock settlement path still runs, but
                // we ask the server whether THIS token can settle
                // before we open the payment overlay. A rotated PIN,
                // an unlock-swap to a steward, or a role change from
                // cashier to steward will refuse here with a 403 —
                // the client cache cannot forge the answer.
                const posToken = window.localStorage.getItem("pos_token");
                if (posToken) {
                  try {
                    let terminal_id = "unbound";
                    try {
                      const b = JSON.parse(window.localStorage.getItem("pos_terminal_binding") || "null");
                      if (b?.terminal_id) terminal_id = b.terminal_id;
                    } catch (_) { /* keep default */ }
                    await axios.post(`${POS_API}/api/pos/auth/authorise-settlement`,
                      {}, { headers: { Authorization: `Bearer ${posToken}`,
                                       "X-Pos-Terminal": terminal_id } });
                  } catch (e) {
                    // Refuse the overlay and surface a human message.
                    const reason = e?.response?.data?.detail
                      || "Settlement not permitted at this device.";
                    sonnerToast.error(reason, { duration: 4000 });
                    return;
                  }
                }
                setPaymentOpen(true);
              }}
              disabled={order.state !== ORDER_STATE.PRINTED || !canSettle || !hasOpenTill || hasSplitChildren}
              disabledReason={hasSplitChildren ? "Order was split — settle each child bill separately." : tillReason}
              primary={order.state === ORDER_STATE.PRINTED && canSettle && hasOpenTill && !hasSplitChildren} />
            <ActionBtn testid="pos-action-repeat" icon={RotateCcw} label="Repeat"
              onClick={onRepeat}
              disabled={locked || lines.length === 0}
              disabledReason={lines.length === 0 ? "Add at least one line to repeat" : null} />
            <ActionBtn testid="pos-action-hold" icon={Pause} label="Hold"
              onClick={() => {
                if (livePos) livePos.holdActive(order.id);
                else dispatch({ type: ACT.ORDER_HELD });
                if (onBack) onBack();
              }}
              disabled={locked || lines.length === 0 || order.state !== ORDER_STATE.OPEN} />
            <ActionBtn testid="pos-action-discount" icon={Percent} label="Discount"
              onClick={() => {
                // POS Control Sprint · Discount stays available on a
                // PRINTED order. Mirrors the Add-Items-after-Print flow
                // exactly: Manager PIN → void the current invoice → the
                // order hydrates back to OPEN → the DiscountDialog is
                // opened once the void completes → next Print Bill
                // allocates a fresh invoice number. The original
                // invoice remains immutable and VOIDED with reason
                // POST_PRINT_DISCOUNT for the Exception Report.
                if (order.state === ORDER_STATE.PRINTED) {
                  requestAuth(
                    `Discount after print — invoice ${order.invoice_no || order.id} will be VOIDED and a new invoice number allocated after the discount is applied.`,
                    async (auth) => {
                      if (livePos) {
                        const r = await livePos.voidInvoiceForReopen({
                          order_id:               order.id,
                          invoice_no:             order.invoice_no,
                          reason:                 "POST_PRINT_DISCOUNT",
                          authorised_by_staff_id: auth.authorised_by_id || auth.authorised_by,
                        });
                        if (!r?.ok) return;
                      } else {
                        dispatch({ type: ACT.INVOICE_VOIDED, payload: {
                          source_order_id: order.id,
                          voided_invoice:  order.invoice_no,
                          authorised_by:   auth.authorised_by,
                          at:              auth.at,
                          reason_code:     "POST_PRINT_DISCOUNT",
                          note:            auth.reason_code || null,
                        }});
                      }
                      // Open the shared Discount dialog once the order
                      // has been safely reopened by the server (or the
                      // demo reducer).
                      setDiscountOpen(true);
                    },
                    "Reason (optional)"
                  );
                  return;
                }
                setDiscountOpen(true);
              }}
              disabled={
                (order.state !== ORDER_STATE.OPEN && order.state !== ORDER_STATE.PRINTED)
                || lines.length === 0
              } />
            {/* Split · Contract §3: OPEN → direct split; PRINTED → PIN unlock inside the dialog. */}
            <ActionBtn testid="pos-action-split" icon={Split} label="Split"
              onClick={() => setSplitOpen(true)}
              disabled={lines.length === 0 || order.state === ORDER_STATE.SETTLED || order.state === ORDER_STATE.VOIDED} />
            <ActionBtn testid="pos-action-transfer" icon={ArrowLeftRight} label="Transfer"
              onClick={() => setTransferOpen(true)}
              disabled={locked || lines.length === 0 || order.state !== ORDER_STATE.OPEN} />
            <ActionBtn testid="pos-action-reprint" icon={Printer} label="Reprint"
              onClick={() => setReprintReasonOpen(true)}
              disabled={!order.invoice_no || order.state === ORDER_STATE.VOIDED} />
            {/* Part 6 (Sprint 4) · Reopen printed bill so more items
                can be added. Emits INVOICE_VOIDED against the printed
                invoice (reusing the same mechanism used for post-print
                split — no parallel mechanism), returns the order to
                OPEN, and the table to OCCUPIED. The next print will
                allocate a fresh invoice number covering ALL items. */}
            {order.state === ORDER_STATE.PRINTED && (
              <ActionBtn testid="pos-action-add-items" icon={PlusIcon} label="Add items"
                alertColor="amber"
                onClick={() => requestAuth(
                  // Financial Ground Truth (Feb 2026) · clarify to the
                  // manager that reopening voids the printed invoice
                  // and produces a new invoice number after printing.
                  `The current invoice ${order.invoice_no || order.id} will be voided and a new invoice number will be generated after items are added. Proceed?`,
                  async (auth) => {
                    if (livePos) {
                      await livePos.voidInvoiceForReopen({
                        order_id:               order.id,
                        invoice_no:             order.invoice_no,
                        reason:                 auth.reason_code || "REOPENED_FOR_ADDITIONAL_ITEMS",
                        authorised_by_staff_id: auth.authorised_by_id || auth.authorised_by,
                      });
                    } else {
                      dispatch({ type: ACT.INVOICE_VOIDED, payload: {
                        source_order_id: order.id,
                        voided_invoice:  order.invoice_no,
                        authorised_by:   auth.authorised_by,
                        at:              auth.at,
                        reason_code:     "REOPENED_FOR_ADDITIONAL_ITEMS",
                        note:            auth.reason_code || null,
                      }});
                    }
                  },
                  "Reason (optional)"
                )} />
            )}
            {/* Sprint 4 correction 4 · Old action-bar Open item button
                removed. Open items are a managed category surfaced in
                the menu area. */}
            {/* Void — enabled at OPEN or PRINTED per §3 of the Contract.
                Disabled at SETTLED (needs a credit note) and VOIDED
                (already terminal). Disabled reason surfaces as a
                tooltip on the button. */}
            {/* POS Cleanup Pass 1 · Clear & Release. Available only
                while every active line is UNSENT (ticket_id == null).
                Once ANY line has been fired, this shortcut is hidden
                and the operator must use Void (kitchen cancellation
                path). No Manager PIN, no reason-code workflow. */}
            {order.state === ORDER_STATE.OPEN
             && lines.length > 0
             && lines.every((l) => l.ticket_id == null) && (
              <ActionBtn testid="pos-action-clear-release" icon={Trash2} label="Clear & release"
                onClick={async () => {
                  // eslint-disable-next-line no-alert
                  const ok = window.confirm("These items have not been sent to kitchen/bar. Clear the order and release the table?");
                  if (!ok) return;
                  if (livePos) {
                    const r = await livePos.releaseDraft(order.id, "UNSENT_DRAFT_RELEASE");
                    if (!r?.ok) return;
                  } else {
                    dispatch({ type: ACT.ORDER_DRAFT_RELEASED, payload: { order_id: order.id, reason: "UNSENT_DRAFT_RELEASE" }});
                  }
                  dispatch({ type: ACT.NAVIGATE_TO_FLOOR });
                }} />
            )}
            <ActionBtn testid="pos-action-void-order" icon={Trash2} label="Void"
              onClick={() => setVoidDlg(true)}
              disabled={order.state === ORDER_STATE.SETTLED
                     || order.state === ORDER_STATE.VOIDED
                     || lines.length === 0}
              disabledReason={
                order.state === ORDER_STATE.SETTLED ? "Settled — reversal requires a credit note"
                : order.state === ORDER_STATE.VOIDED ? "Already voided"
                : null} />
          </div>

          {/* State banners */}
          {order.state === ORDER_STATE.PRINTED && (
            <div className="border-t border-purple-200 bg-purple-50 px-3 py-1 text-[11px] text-purple-900"
              data-testid="pos-locked-banner">
              Bill printed{order.invoice_no ? ` · ${order.invoice_no}` : ""} — editing locked. Settle or Void to close.
            </div>
          )}
          {order.state === ORDER_STATE.SETTLED && (
            <div className="border-t border-emerald-200 bg-emerald-50 px-3 py-1 text-[11px] text-emerald-900"
              data-testid="pos-settled-banner">
              Settled · read-only. Reprint available.
            </div>
          )}
          {order.state === ORDER_STATE.VOIDED && (
            <div className="border-t border-rose-200 bg-rose-50 px-3 py-1 text-[11px] text-rose-900"
              data-testid="pos-voided-banner">
              Voided{order.invoice_no ? ` · ${order.invoice_no}` : ""} · reason {order.void_reason} · by {order.void_authorised_by}
            </div>
          )}
        </div>

        {/* ─── RIGHT: menu ─── */}
        {/* ─── RIGHT: menu + search + categories + item grid ─────
            The right pane owns the overflow. `min-w-0` is required so
            children with wide intrinsic content (the category strip)
            can shrink and let their own `overflow-x-auto` scroll,
            instead of pushing into the ticket panel on the left.
        ─────────────────────────────────────────────────────────── */}
        <div className="flex-1 min-w-0 flex flex-col">
          {/* Search */}
          <div className="border-b border-[color:var(--pos-color-border,#E2E8DF)] px-3 py-2 bg-white flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="w-4 h-4 text-[color:var(--pos-color-secondary-text,#5C6B64)] absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input ref={searchRef} type="text" value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search items · Ctrl+F"
                className="w-full h-11 pl-8 pr-8 rounded border border-[color:var(--pos-color-border,#E2E8DF)] bg-[color:var(--pos-color-raised-surface,#F9F9F8)] text-[13px] text-[color:var(--pos-color-primary-text,#1A2421)] focus:border-[color:var(--pos-color-accent,var(--color-primary))] focus:outline-none"
                data-testid="pos-search" />
              {q && (
                <button type="button" onClick={() => setQ("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-[#EEF0EC]"
                  data-testid="pos-search-clear" aria-label="Clear">
                  <XIcon className="w-3.5 h-3.5 text-[color:var(--pos-color-secondary-text,#5C6B64)]" />
                </button>
              )}
            </div>
          </div>

          {/* Category strip — hidden while searching. Structural rule
              (Sprint 4 correction 1b) — the strip container carries
              `overflow-x-auto`; the inner track carries `min-w-max` so
              extra categories scroll horizontally inside the RIGHT
              pane rather than pushing into the ticket panel. */}
          {!q && (
            <div className="border-b border-[color:var(--pos-color-border,#E2E8DF)] bg-white overflow-x-auto"
              data-testid="pos-category-strip">
              <div className="flex px-2 py-2 gap-1.5 min-w-max">
                {CATEGORIES.map((c) => (
                  <button key={c.id} type="button" onClick={() => setCat(c.id)}
                    data-testid={`pos-category-${c.id}`}
                    className={`min-h-[44px] px-3 rounded border text-[12px] font-medium whitespace-nowrap transition
                      ${cat === c.id
                        ? "border-[color:var(--pos-color-accent,var(--color-primary))] bg-[color:var(--pos-color-selected)] text-[color:var(--pos-color-on-selected,#1A2421)]"
                        : "border-[color:var(--pos-color-border,#E2E8DF)] bg-white text-[color:var(--pos-color-primary-text,#1A2421)] hover:bg-[color:var(--pos-color-raised-surface,#F9F9F8)]"}`}>
                    <span className="mr-1.5">{c.icon}</span>{c.name}
                  </button>
                ))}
                {/* Sprint 4 correction 4 · Managed "Open items" tab. */}
                <button type="button" onClick={() => setCat("cat_open_items")}
                  data-testid="pos-category-cat_open_items"
                  className={`min-h-[44px] px-3 rounded border text-[12px] font-medium whitespace-nowrap transition
                    ${cat === "cat_open_items"
                      ? "border-emerald-600 bg-emerald-50 text-emerald-900"
                      : "border-emerald-200 bg-white text-emerald-800 hover:bg-emerald-50"}`}>
                  <span className="mr-1.5">✳</span>Open items · {(state.session.managed_open_items || []).length}
                </button>
              </div>
            </div>
          )}

          {/* Item grid — the ONLY scrolling region on the right */}
          <div className="flex-1 overflow-y-auto p-3" data-testid="pos-item-grid">
            {cat === "cat_open_items" && !q && (
              <div className="text-[11px] text-[color:var(--pos-color-secondary-text,#5C6B64)] mb-2 italic" data-testid="pos-open-items-hint">
                {(state.session.managed_open_items || []).length === 0
                  ? "No open items yet. Manager PIN needed to create one — entries persist for the business day."
                  : "Managed open items. Tap to punch. Entries clear when the business day closes."}
              </div>
            )}
            {filteredItems.length === 0 && cat !== "cat_open_items" && (
              <div className="text-center text-[12px] text-[color:var(--pos-color-secondary-text,#5C6B64)] py-8">
                No items match {'"'}{q}{'"'}.
              </div>
            )}
            <div className="grid grid-cols-4 gap-2">
              {cat === "cat_open_items" && !q && (
                <button type="button"
                  onClick={() => {
                    // Sprint 4 correction 4 · Manager PIN gates CREATION.
                    requestAuth(
                      "Create a managed open item · ad-hoc pricing for the business day",
                      () => setOpenItemOpen(true),
                      "Reason (optional)",
                    );
                  }}
                  disabled={locked || !state.session.business_date}
                  data-testid="pos-open-item-create"
                  className="min-h-[80px] rounded border-2 border-dashed border-emerald-400 bg-emerald-50/40 text-left p-2.5 hover:bg-emerald-50 active:bg-emerald-100 disabled:opacity-40 disabled:cursor-not-allowed transition flex flex-col items-start justify-center gap-1">
                  <div className="text-[12px] font-semibold text-emerald-800 leading-tight">
                    + Create open item
                  </div>
                  <div className="text-[10px] text-emerald-700">Manager PIN required</div>
                </button>
              )}
              {filteredItems.map((it) => (
                <button key={it.id} type="button" onClick={() => onTileTap(it)}
                  disabled={locked}
                  data-testid={`pos-item-${it.id}`}
                  style={{
                    // Premium item-card refinement — 12px radius. Elevation
                    // and box-shadow now come from the CSS layer so the
                    // hover/:focus-visible states can override cleanly
                    // (inline styles win over stylesheet rules, and the
                    // POS Control sprint requires Tab-focus to visibly
                    // raise + carry the themed ring).
                    borderRadius: "12px",
                  }}
                  className="pos-item-tile min-h-[80px] border border-[color:var(--pos-color-border,#E2E8DF)] bg-white text-left p-2.5 hover:bg-[color:var(--pos-color-raised-surface,#F9F9F8)] active:bg-[#F0F0EE] disabled:opacity-40 relative transition focus:outline-none">
                  <div className="text-[12px] font-medium text-[color:var(--pos-color-primary-text,#1A2421)] leading-tight line-clamp-2 min-h-[2.4em]">
                    {it.name}
                  </div>
                  <div className="text-[14px] font-bold text-[color:var(--pos-color-primary-text,#1A2421)] tabular-nums mt-1">{fmtInt(it.price)}</div>
                  {it._open_item && (
                    <div className="text-[9px] uppercase tracking-wider text-emerald-800 bg-emerald-100 border border-emerald-200 rounded px-1 py-[1px] mt-1 inline-block">
                      {it._open_item.category}
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <PrintUnfiredDialog
        open={printDlg}
        unfiredCount={lines.filter((l) => l.ticket_id == null).length}
        onCancel={() => setPrintDlg(false)}
        onSendAndPrint={() => {
          if (livePos) livePos.fireTicket(order.id);
          else dispatch({ type: ACT.TICKET_SENT });
          setPrintDlg(false);
          setPreviewMode("print");
          setPreviewOpen(true);
        }}
        onDropAndPrint={() => {
          lines.filter((l) => l.ticket_id == null).forEach((l) => {
            if (livePos) livePos.voidLine(order.id, l.id);
            else dispatch({ type: ACT.LINE_VOIDED, payload: { line_id: l.id } });
          });
          setPrintDlg(false);
          setPreviewMode("print");
          setPreviewOpen(true);
        }} />

      <CancellationSlipDialog
        slip={cancelSlip}
        onClose={() => setCancelSlip(null)} />

      {/* POS Control Sprint · Reprint reason capture then Manager PIN.
          Chains into requestAuth once the reason is confirmed so the
          existing SharedPinDialog + `livePos.reprintBill` path runs
          exactly as it did for un-audited reprints — no parallel
          endpoint, no second print flow. */}
      <ReprintReasonDialog
        open={reprintReasonOpen}
        invoiceNo={order.invoice_no}
        onCancel={() => setReprintReasonOpen(false)}
        onConfirm={({ reason_code, notes }) => {
          setReprintReasonOpen(false);
          requestAuth(
            `Reprint invoice ${order.invoice_no || order.id} · reason ${reason_code}${notes ? " · " + notes : ""}`,
            async (auth) => {
              const audit = {
                reason_code,
                notes:            notes || null,
                authorised_by:    auth.authorised_by,
                authorised_by_id: auth.authorised_by_id,
                at:               auth.at,
              };
              if (livePos) {
                const ok = await livePos.reprintBill(order.id, audit);
                if (!ok) return;
              } else {
                dispatch({ type: ACT.BILL_REPRINTED, payload: audit });
              }
              // Show the reprinted preview after the audit has landed
              // so the receipt watermark reads REPRINT with the count.
              setPreviewMode("reprint");
              setPreviewOpen(true);
            },
          );
        }} />

      <VoidConfirmDialog
        open={voidDlg}
        order={order}
        onCancel={() => setVoidDlg(false)}
        onConfirm={(payload) => {
          // POS Cleanup Pass 1 · voiding a whole order is now a
          // canonical server action. VoidConfirmDialog captures the
          // reason + note; requestAuth captures the manager PIN;
          // livePos.voidOrder cascades the invoice void + line void
          // + order state atomically. The reducer mirror is fired
          // via LIVE_ORDER_HYDRATED inside the thunk so refresh /
          // logout still sees the order VOIDED.
          setVoidDlg(false);
          requestAuth(
            `Void whole order · ${order.invoice_no || order.id}`,
            async (auth) => {
              const reasonText = payload?.note
                ? `${payload.reason || "OTHER"} · ${payload.note}`
                : (payload.reason || "OTHER");
              if (livePos) {
                const r = await livePos.voidOrder({
                  order_id:               order.id,
                  reason:                 reasonText,
                  authorised_by_staff_id: auth.authorised_by_id || auth.authorised_by,
                });
                if (!r?.ok) return;
              } else {
                dispatch({ type: ACT.ORDER_VOIDED, payload: {
                  ...payload,
                  authorised_by: auth.authorised_by,
                  void_at:       auth.at,
                }});
              }
            },
          );
        }} />

      <SharedPinDialog
        open={!!pinAction}
        action_label={pinAction?.label || ""}
        reason_prompt={pinAction?.reason_prompt}
        onCancel={() => setPinAction(null)}
        onConfirm={(auth) => {
          try { pinAction?.run?.(auth); }
          finally { setPinAction(null); }
        }} />

      {/* Sprint 8 · POS-QCS · manager PIN for qty > 999 */}
      <QuantityElevationDialog
        open={!!qtyElevation}
        kind="quantity"
        intendedValue={qtyElevation?.intendedValue}
        previousValue={qtyElevation?.previousValue}
        order_id={order?.id}
        line_id={qtyElevation?.line_id || null}
        initiator_staff_id={getActor(state)?.staff_id || null}
        API={process.env.REACT_APP_BACKEND_URL}
        headers={(() => {
          const posToken = typeof window !== "undefined"
            ? window.localStorage.getItem("pos_token") : null;
          let terminal_id = "unbound";
          try {
            const b = JSON.parse(window.localStorage.getItem("pos_terminal_binding") || "null");
            if (b?.terminal_id) terminal_id = b.terminal_id;
          } catch (_) { /* keep default */ }
          return {
            "Content-Type": "application/json",
            "X-Pos-Terminal": terminal_id,
            ...(posToken ? { Authorization: `Bearer ${posToken}` } : {}),
          };
        })()}
        onCancel={() => setQtyElevation(null)}
        onConfirm={(auth) => {
          const run = qtyElevation?.onApproved;
          setQtyElevation(null);
          if (run) run(auth);
        }} />

      <PaymentOverlay
        open={paymentOpen}
        state={state}
        dispatch={dispatch}
        livePos={livePos}
        requestAuth={requestAuth}
        onCancel={() => setPaymentOpen(false)}
        onDone={() => {
          // Cashier explicitly acknowledged the settlement. Close
          // the overlay and let the terminal orchestrate the
          // post-settle cleanup (dine-in → TABLE_MARKED_DIRTY + reset;
          // takeaway/delivery/room → fresh empty order). No timer.
          setPaymentOpen(false);
          // Part 3.3 · if this was a child payment, hand control
          // back to the split view so the cashier never loses sight
          // of the remaining siblings.
          if (childPayOrderId) {
            const wasChildOf = state.orders?.[childPayOrderId]?.order?.split_from;
            setChildPayOrderId(null);
            if (wasChildOf) {
              // Reactivate the parent order id so the split view can
              // reload its children list.
              dispatch({ type: ACT.ORDER_ACTIVATED, payload: { order_id: wasChildOf } });
              setSplitViewParentId(wasChildOf);
              setSplitViewOpen(true);
              return; // do NOT run the takeaway/dine-in cleanup path
            }
          }
          if (onSettle) onSettle();
        }} />

      <KitchenNoteDialog
        line={noteDlgLine}
        onCancel={() => setNoteDlgLine(null)}
        onSave={(note) => {
          if (noteDlgLine) {
            if (livePos) livePos.setLineNote(order.id, noteDlgLine.id, note);
            else dispatch({ type: ACT.LINE_NOTE_SET, payload: { line_id: noteDlgLine.id, note } });
          }
          setNoteDlgLine(null);
        }} />

      {/* Sprint 2 Correction Part 1 · Send-ticket confirmation. Lists
          exactly what will be fired — destination, table/token, covers,
          steward, items + qty + kitchen notes. Cancel closes and fires
          NOTHING. Confirm actually fires. No second dialog after. */}
      <SendKotConfirmDialog
        open={kotConfirmOpen}
        order={order}
        lines={lines.filter((l) => l.ticket_id == null)}
        stewardName={stewardName}
        onCancel={() => setKotConfirmOpen(false)}
        onConfirm={() => {
          setKotConfirmOpen(false);
          if (livePos) livePos.fireTicket(order.id);
          else dispatch({ type: ACT.TICKET_SENT });
          sonnerToast.success("Ticket fired", { duration: 1500 });
        }} />

      {heldTrayOpen && (
        <BillingHeldTray
          held={held}
          onClose={() => setHeldTrayOpen(false)}
          onResume={(order_id) => {
            dispatch({ type: ACT.ORDER_ACTIVATED, payload: { order_id } });
            if (livePos) livePos.resumeActive(order_id);
            else dispatch({ type: ACT.ORDER_RESUMED });
            setHeldTrayOpen(false);
          }} />
      )}

      {/* Part 3.2/3.3 · Split children overview */}
      <SplitChildrenView
        open={splitViewOpen}
        state={state}
        parentId={splitViewParentId}
        onClose={() => setSplitViewOpen(false)}
        onPrintChild={(child_id) => {
          dispatch({ type: ACT.ORDER_ACTIVATED, payload: { order_id: child_id } });
          const childSlice = state.orders?.[child_id];
          const childHasUnsent = (childSlice?.lines || []).some((l) => l.ticket_id == null);
          if (livePos) {
            // Server path — ticket must already be fired for the invoice
            // endpoint to succeed, so send it first when necessary.
            if (childHasUnsent) livePos.fireTicket(child_id);
            livePos.printBill(child_id);
          } else {
            // Demo path · fire the ticket if needed so the reducer's
            // BILL_PRINTED transition (which refuses on unsent lines)
            // can proceed for the newly-activated child order.
            if (childHasUnsent) dispatch({ type: ACT.TICKET_SENT });
            dispatch({ type: ACT.BILL_PRINTED });
          }
        }}
        onReprintChild={(child_id) => {
          // POS Control Sprint · reprint per child. Activate the child
          // then drive the same reprint-reason + PIN flow the parent
          // uses. Every reprint records against the child's own
          // invoice number via the existing endpoint — no parallel
          // print/reprint paths for split children.
          dispatch({ type: ACT.ORDER_ACTIVATED, payload: { order_id: child_id } });
          setSplitViewOpen(false);
          setTimeout(() => setReprintReasonOpen(true), 30);
        }}
        onPayChild={(child_id) => {
          dispatch({ type: ACT.ORDER_ACTIVATED, payload: { order_id: child_id } });
          setSplitViewOpen(false);
          setChildPayOrderId(child_id);
          setPaymentOpen(true);
        }}
        onOpenPreview={(child_id) => {
          dispatch({ type: ACT.ORDER_ACTIVATED, payload: { order_id: child_id } });
          setSplitViewOpen(false);
          setPreviewMode("send");
          setPreviewOpen(true);
        }} />

      {/* Part 5.1/5.2 · Guest details capture (phone-first, lookup,
          consent, delivery address). */}
      <GuestDetailsDialog
        open={guestDlgOpen}
        order={order}
        onCancel={() => setGuestDlgOpen(false)}
        onSave={(payload) => {
          dispatch({ type: ACT.GUEST_DETAILS_UPDATED, payload });
          setGuestDlgOpen(false);
        }} />

      {/* Part 7.1 · Cancellation reason before qty-dec on a fired line.
          Sprint 4 Part 5 · fired-line cancellation ALSO requires the
          manager PIN on top of the reason capture — the kitchen has
          already cooked it. Reason is captured first (so the PIN
          dialog carries the reason label), PIN is captured second,
          and both flow through into TICKET_ITEM_CANCELLED so the
          exception report and Z report attribute correctly. */}
      <CancellationReasonDialog
        line={cancelReasonLine}
        onCancel={() => setCancelReasonLine(null)}
        onConfirm={({ reason_code, note }) => {
          const target = cancelReasonLine;
          setCancelReasonLine(null);
          if (!target) return;
          // Item 4 (Sprint 4 corrections) · `_remove_all` marker set
          // when the Remove-line action was invoked (as opposed to a
          // qty decrement). Sets qty to 0 so the whole line is
          // cancelled; the reducer emits TICKET_ITEM_CANCELLED with
          // qty_cancelled = old_qty and prints a cancellation slip.
          const nextQty = target._remove_all ? 0 : Math.max(0, target.qty - 1);
          // Sprint 4 correction 2 · When the order is PRINTED, prepend
          // INVOICE_VOIDED to the same PIN callback that fires the
          // LINE_QTY_CHANGED. The reducer processes each dispatch
          // sequentially against the latest state, so the reopen
          // flips OPEN before the line-change lands.
          requestAuth(
            `Void a fired line · ${target.item_name} · reason: ${reason_code}`,
            (auth) => {
              if (order.state === ORDER_STATE.PRINTED) {
                dispatch({ type: ACT.INVOICE_VOIDED, payload: {
                  source_order_id: order.id,
                  voided_invoice:  order.invoice_no,
                  authorised_by:   auth.authorised_by,
                  at:              auth.at,
                  reason_code:     "REOPENED_FOR_LINE_EDIT",
                  note:            reason_code || null,
                }});
              }
              dispatch({ type: ACT.LINE_QTY_CHANGED, payload: {
                line_id: target.id,
                qty: nextQty,
                reason_code, note,
                authorised_by: auth.authorised_by,
                at:            auth.at,
              }});
            },
          );
        }} />

      <BillPreviewDialog
        open={previewOpen}
        state={state}
        outletName={outletName}
        mode={previewMode}
        onCancel={() => setPreviewOpen(false)}
        onConfirmPrint={() => {
          if (previewMode === "reprint") {
            // POS Control Sprint · the reprint audit + counter bump was
            // already recorded by the Reprint action flow. The preview's
            // Print button in reprint mode is a "close and dispatch to
            // printer" no-op — we DO NOT re-invoke reprintBill here to
            // avoid double-counting the reprint event.
          } else {
            // Sprint 3B · route through the server so the invoice number
            // on the guest's bill is the REAL fiscal number, not the
            // reducer's local counter. livePos.printBill dispatches
            // BILL_PRINTED with the server invoice_no already attached.
            if (livePos) livePos.printBill(order.id);
            else dispatch({ type: ACT.BILL_PRINTED });
          }
          setPreviewOpen(false);
        }} />

      <DiscountDialog
        open={discountOpen}
        order={order}
        totals={totals}
        onCancel={() => setDiscountOpen(false)}
        onApply={(payload) => {
          // Part 5 (Sprint 4) · discounts ABOVE the ceiling require the
          // manager PIN. Below-ceiling discounts apply directly. The
          // ceiling is computed against the eligible base surfaced by
          // the discount dialog. Membership + voucher redemptions
          // carry their own authoriser stamp and skip the PIN.
          if (payload && payload.reason !== "MEMBERSHIP_REDEMPTION" && payload.reason !== "GIFT_VOUCHER") {
            const eligible = totals.order_discount_eligible_base ?? totals.net_subtotal ?? 0;
            const rupees = payload.type === "percent"
              ? eligible * (Number(payload.value || 0) / 100)
              : Number(payload.value || 0);
            const pct = eligible > 0 ? (rupees / eligible) * 100 : 0;
            if (pct > POS_CONFIG.discount_ceiling_pct) {
              setDiscountOpen(false);
              requestAuth(
                `Discount ${pct.toFixed(1)}% above ceiling (${POS_CONFIG.discount_ceiling_pct}%) — ₹${Number(rupees).toFixed(2)}`,
                (auth) => dispatch({ type: ACT.DISCOUNT_APPLIED, payload: {
                  ...payload,
                  reason: payload.reason || "ABOVE_CEILING",
                  authorised_by: auth.authorised_by,
                }}),
                "Reason for high discount"
              );
              return;
            }
          }
          dispatch({ type: ACT.DISCOUNT_APPLIED, payload });
          setDiscountOpen(false);
        }} />

      {/* Part 3 · line-scoped discount opens the SAME dialog, capped at line net. */}
      <DiscountDialog
        open={!!lineDiscountFor}
        order={{ discount: null }}
        totals={{
          net_subtotal: lineDiscountFor ? (lineDiscountFor.qty * lineDiscountFor.unit_price - Number(lineDiscountFor.discount || 0)) : 0
        }}
        onCancel={() => setLineDiscountFor(null)}
        onApply={(payload) => {
          if (lineDiscountFor && payload) {
            const gross = lineDiscountFor.qty * lineDiscountFor.unit_price;
            const amt = payload.type === "percent"
              ? gross * (Math.min(100, Number(payload.value || 0)) / 100)
              : Math.min(Number(payload.value || 0), gross);
            dispatch({ type: ACT.LINE_DISCOUNT_SET, payload: { line_id: lineDiscountFor.id, discount: amt } });
          } else if (lineDiscountFor && !payload) {
            dispatch({ type: ACT.LINE_DISCOUNT_SET, payload: { line_id: lineDiscountFor.id, discount: 0 } });
          }
          setLineDiscountFor(null);
        }} />

      <OpenItemDialog
        open={openItemOpen}
        mode="create"
        onCancel={() => setOpenItemOpen(false)}
        onPunch={(payload) => {
          // Sprint 4 correction 4 · Create a business-day-scoped
          // catalog entry, not a live line. Cashiers punch the new
          // entry from the "Open items" category thereafter.
          dispatch({ type: ACT.OPEN_ITEM_CREATED, payload: {
            name: payload.name,
            price: payload.price,
            category: payload.category,
            authorised_by: userName || "system",
          }});
          setOpenItemOpen(false);
        }} />

      <SplitDialog
        open={splitOpen}
        state={state}
        order={order}
        lines={lines}
        onCancel={() => setSplitOpen(false)}
        onConfirm={async (payload) => {
          // POS Control Sprint · always persist the split on the
          // server when we're bound to a live backend. The server
          // returns fresh order ids for every child and preserves the
          // parent's ticket assignments so the subsequent Print Bill
          // on any child goes straight through the invoice endpoint
          // — no "Order not found", no duplicate KOT.
          if (livePos) {
            const r = await livePos.splitOrder({ order_id: order.id, groups: payload.groups });
            if (!r?.ok) return;
          } else {
            dispatch({ type: ACT.ORDER_SPLIT, payload: { source_order_id: order.id, ...payload } });
          }
          setSplitOpen(false);
          // Part 3.2 · surface the split children view immediately so
          // the cashier can print + settle each child.
          setSplitViewParentId(order.id);
          setSplitViewOpen(true);
        }}
        onRequestPinUnlock={() => setPinOpen(true)} />

      <ManagerPinDialog
        open={pinOpen}
        onCancel={() => setPinOpen(false)}
        onOk={(pin) => {
          dispatch({ type: ACT.INVOICE_VOIDED, payload: { source_order_id: order.id, pin, authorised_by: userName || "system", reason_code: "POST_PRINT_SPLIT" }});
          setPinOpen(false);
        }} />

      <TransferDialog
        open={transferOpen}
        state={state}
        order={order}
        lines={lines}
        onCancel={() => setTransferOpen(false)}
        onTransferTable={(to_table_id) => {
          dispatch({ type: ACT.TABLE_TRANSFERRED, payload: { order_id: order.id, to_table_id } });
          setTransferOpen(false);
        }}
        onTransferItems={(dest_table_id, line_ids) => {
          const destOrder = activeOrderForTable(state, dest_table_id);
          if (!destOrder) { setTransferOpen(false); return; }
          line_ids.forEach((line_id) => {
            dispatch({ type: ACT.ITEM_TRANSFERRED, payload: {
              source_order_id: order.id, line_id, dest_order_id: destOrder.order.id,
            }});
          });
          setTransferOpen(false);
        }} />
    </div>
  );
}

// ─── Void confirmation dialog · Wired to shared ReasonInput
//     (Part 11). Voids collect a preset code plus optional free-text
//     note; the payload dispatched matches ORDER_VOIDED expectations.
function VoidConfirmDialog({ open, order, onCancel, onConfirm }) {
  const [reason, setReason] = useReasonState();
  useEffect(() => { if (open) setReason({ reason_code: null, note: "" }); }, [open, setReason]);
  if (!open) return null;
  const printed = order.state === ORDER_STATE.PRINTED;
  const submit = () => {
    if (!reasonValid(reason)) return;
    const n = normaliseReason(reason);
    onConfirm({ reason_code: n.reason_code, note: n.note, authorised_by: "system" });
  };
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onCancel(); }}>
      <DialogContent className="max-w-md p-0 gap-0 border border-rose-200 overflow-hidden"
        data-testid="pos-void-confirm-dialog">
        <div className="px-5 py-4 border-b border-rose-100 bg-rose-50">
          <div className="flex items-center gap-2 text-rose-800 mb-1">
            <AlertTriangle className="w-4 h-4" />
            <span className="text-[10px] uppercase tracking-wider">Void order</span>
          </div>
          <div className="text-[15px] font-semibold text-[color:var(--pos-color-primary-text,#1A2421)]">
            Void this order?
          </div>
          {printed && order.invoice_no && (
            <div className="text-[12px] text-rose-900 mt-1 tabular-nums" data-testid="pos-void-invoice-no">
              Invoice <strong>{order.invoice_no}</strong> has been consumed and will be recorded as void.
            </div>
          )}
          {!printed && (
            <div className="text-[12px] text-rose-900 mt-1">
              Order state is <strong>{order.state}</strong> — no invoice number consumed yet.
            </div>
          )}
        </div>
        <div className="p-4 space-y-3">
          <ReasonInput
            presets={VOID_PRESETS}
            value={reason}
            onChange={setReason}
            testidPrefix="pos-void" />
          <div className="text-[11px] text-[color:var(--pos-color-secondary-text,#5C6B64)] italic">
            Authorised by <strong className="text-[color:var(--pos-color-primary-text,#1A2421)] tabular-nums">system</strong>
          </div>
        </div>
        <div className="p-3 border-t border-[color:var(--pos-color-border,#E2E8DF)] flex items-center gap-2 justify-end">
          <button type="button" onClick={onCancel}
            data-testid="pos-void-cancel"
            className="min-h-[44px] px-4 rounded text-[12px] font-medium text-[color:var(--pos-color-secondary-text,#5C6B64)] hover:bg-[color:var(--pos-color-page-bg,#F4F6F2)] transition">
            Cancel
          </button>
          <button type="button"
            onClick={submit}
            disabled={!reasonValid(reason)}
            data-testid="pos-void-confirm"
            className="min-h-[44px] px-4 rounded border border-transparent bg-rose-700 text-white text-[13px] font-medium hover:bg-rose-800 transition disabled:opacity-40 disabled:cursor-not-allowed">
            Void order
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Cancellation-slip dialog · shown when a fired-line qty is
//     reduced. Mock evidence that a TICKET_ITEM_CANCELLED was
//     recorded — the real cashier flow prints a slip on the ticket
//     printer with reason + authoriser fields signed by a manager.
function CancellationSlipDialog({ slip, onClose }) {
  if (!slip) return null;
  const at = new Date(slip.at);
  return (
    <Dialog open={!!slip} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-sm p-0 gap-0 border border-rose-200 overflow-hidden"
        data-testid="pos-cancel-slip-dialog">
        <div className="px-5 py-4 border-b border-rose-100 bg-rose-50 rounded-t-lg">
          <div className="text-[10px] uppercase tracking-wider text-rose-800 mb-1">Kitchen cancellation slip</div>
          <div className="text-[14px] font-semibold text-[color:var(--pos-color-primary-text,#1A2421)]">
            {slip.qty_cancelled} × {slip.item_name}
          </div>
          <div className="text-[11px] text-rose-800 mt-0.5">Ref: {slip.ticket_id}</div>
        </div>
        <div className="px-5 py-3 text-[12px] text-[color:var(--pos-color-primary-text,#1A2421)] leading-relaxed">
          <div className="flex justify-between py-0.5">
            <span className="text-[color:var(--pos-color-secondary-text,#5C6B64)]">Reason code</span>
            <span className="font-medium" data-testid="pos-cancel-slip-reason">{slip.reason_code}</span>
          </div>
          <div className="flex justify-between py-0.5">
            <span className="text-[color:var(--pos-color-secondary-text,#5C6B64)]">Authorised by</span>
            <span className="font-medium tabular-nums" data-testid="pos-cancel-slip-authoriser">{slip.authorised_by}</span>
          </div>
          <div className="flex justify-between py-0.5">
            <span className="text-[color:var(--pos-color-secondary-text,#5C6B64)]">Recorded at</span>
            <span className="font-medium tabular-nums">{at.toLocaleTimeString()}</span>
          </div>
          <div className="mt-2 text-[10px] text-[color:var(--pos-color-secondary-text,#5C6B64)] italic">
            Manager authorisation captured on the source terminal.
          </div>
        </div>
        <div className="p-3 border-t border-[color:var(--pos-color-border,#E2E8DF)]">
          <button type="button" onClick={onClose}
            data-testid="pos-cancel-slip-ok"
            className="w-full min-h-[44px] rounded border border-transparent bg-[color:var(--color-primary)] text-white text-[13px] font-medium hover:bg-[color:var(--color-primary-hover)] transition">
            Acknowledged
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Print with unfired lines · 3-choice dialog ─────────────────────
function PrintUnfiredDialog({ open, unfiredCount, onCancel, onSendAndPrint, onDropAndPrint }) {
  if (!open) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onCancel(); }}>
      <DialogContent className="max-w-md p-0 gap-0 border border-[color:var(--pos-color-border,#E2E8DF)] overflow-hidden"
        data-testid="pos-print-unfired-dialog">
        <div className="px-5 py-4 border-b border-[color:var(--pos-color-border,#E2E8DF)]">
          <div className="text-[10px] uppercase tracking-wider text-amber-800 mb-1">Unsent lines on ticket</div>
          <div className="text-[15px] font-semibold text-[color:var(--pos-color-primary-text,#1A2421)]">
            {unfiredCount} item{unfiredCount === 1 ? "" : "s"} not yet sent to the kitchen
          </div>
          <p className="text-[12px] text-[color:var(--pos-color-secondary-text,#5C6B64)] mt-1 leading-snug">
            Bills can only be printed once every line is fired.
            Choose how to proceed.
          </p>
        </div>
        <div className="p-3 flex flex-col gap-2">
          <button type="button" onClick={onSendAndPrint}
            data-testid="pos-print-choice-send"
            className="w-full min-h-[52px] rounded border border-transparent bg-[color:var(--color-primary)] text-white text-[13px] font-medium px-4 text-left hover:bg-[color:var(--color-primary-hover)] transition">
            <div className="font-semibold">Send to kitchen and print</div>
            <div className="text-[11px] font-normal text-white/80">Fires a new KOT, then prints the bill.</div>
          </button>
          <button type="button" onClick={onDropAndPrint}
            data-testid="pos-print-choice-drop"
            className="w-full min-h-[52px] rounded border border-[color:var(--pos-color-border,#E2E8DF)] bg-white text-[13px] font-medium text-[color:var(--pos-color-primary-text,#1A2421)] px-4 text-left hover:bg-[color:var(--pos-color-raised-surface,#F9F9F8)] transition">
            <div className="font-semibold">Remove unsent lines and print</div>
            <div className="text-[11px] font-normal text-[color:var(--pos-color-secondary-text,#5C6B64)]">Voids the unfired lines, prints the rest.</div>
          </button>
          <button type="button" onClick={onCancel}
            data-testid="pos-print-choice-cancel"
            className="w-full min-h-[44px] rounded text-[12px] font-medium text-[color:var(--pos-color-secondary-text,#5C6B64)] hover:bg-[color:var(--pos-color-page-bg,#F4F6F2)] transition">
            Cancel
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Line row + Totals + Action button ──────────────────────────────
function LineRow({ line, idx, popoverOpen, onOpenPopover, onClosePopover, onOpenNote, onOpenLineDiscount, onOpenCancelReason, onRequestRemove, onRequestUnfiredDec, onQtyRequest, dispatch, livePos, orderId, locked, orderState }) {
  const item = ITEMS.find((x) => x.id === line.item_id);
  const unit = Number(line.unit_price) || 0;
  const lineAmt = Math.max(0, unit * line.qty - (Number(line.discount) || 0));
  const ticketId = line.ticket_id;
  const isUnfired = ticketId == null;
  // Sprint 4 correction 2 · Decrement stays available on PRINTED —
  // caller wraps the dispatch with `withReopenIfPrinted`.
  const decEditable = orderState !== ORDER_STATE.SETTLED
                   && orderState !== ORDER_STATE.VOIDED;

  return (
    <div className={`relative border-b border-[color:var(--pos-color-border,#E2E8DF)] px-3 py-2 flex items-start justify-between gap-2 transition
      ${isUnfired
        ? "bg-amber-50/70 border-l-2 border-l-amber-400"
        : "hover:bg-[color:var(--pos-color-raised-surface,#F9F9F8)]"}`}
      onClick={onOpenPopover}
      data-testid={`pos-line-${idx}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-[12px] font-medium text-[color:var(--pos-color-primary-text,#1A2421)] truncate" data-testid={`pos-line-${idx}-name`}>
            {line.item_name}
          </span>
          {isUnfired && (
            <span className="text-[9px] uppercase tracking-wider text-amber-900 bg-amber-200/70 rounded px-1 py-[1px] shrink-0"
              data-testid={`pos-line-${idx}-unsent-tag`}>NEW</span>
          )}
          {ticketId && (
            <span className={`text-[9px] uppercase tracking-wider border rounded px-1 py-[1px] shrink-0
              ${line.ticket_dest === "bar"
                ? "text-orange-900 bg-orange-100 border-orange-200"
                : "text-sky-900 bg-sky-100 border-sky-200"}`}
              data-testid={`pos-line-${idx}-ticket-tag`}>
              {/* Sprint 2 Correction Part 2 · badge shows KOT_N / BOT_N
                  from the server-stamped ticket_no; NEVER the raw UUID. */}
              {line.ticket_no
                ? `${line.ticket_dest === "bar" ? "BOT" : "KOT"}_${line.ticket_no}`
                : (typeof ticketId === "string" && /^(KOT|BOT)_/.test(ticketId)
                    ? ticketId
                    : `${line.ticket_dest === "bar" ? "BOT" : "KOT"}_?`)}
            </span>
          )}
          {(item?.tax_class === "liquor" || line.open_item_tax_class === "liquor") && (
            <span className="text-[9px] uppercase tracking-wider text-purple-900 bg-purple-100 border border-purple-200 rounded px-1 py-[1px] shrink-0">Liquor</span>
          )}
          {line.is_open_item && (
            <span className="text-[9px] uppercase tracking-wider text-emerald-900 bg-emerald-100 border border-emerald-200 rounded px-1 py-[1px] shrink-0"
              data-testid={`pos-line-${idx}-open-item-tag`}
              title={`Open item · punched by ${line.punched_by || "—"}`}>Open</span>
          )}
          {line._pending && (
            /* Sprint 2 B.2 · in-flight indicator — dots animate until
               the server confirms or rejects the change. */
            <span className="text-[9px] uppercase tracking-wider text-slate-600 bg-slate-100 border border-slate-200 rounded px-1 py-[1px] shrink-0 inline-flex items-center gap-1"
              data-testid={`pos-line-${idx}-pending-tag`} title="Waiting for server confirmation">
              <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-pulse" />
              Saving…
            </span>
          )}
        </div>
        {line.note && <div className="text-[10px] italic text-[color:var(--pos-color-secondary-text,#5C6B64)] mt-0.5 truncate">Note: {line.note}</div>}
        <div className="text-[10px] text-[color:var(--pos-color-secondary-text,#5C6B64)] tabular-nums mt-0.5">
          {line.qty} × ₹{unit.toFixed(0)}
          {line.discount ? <span className="text-rose-700"> − ₹{line.discount.toFixed(0)}</span> : null}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-1">
          <button type="button" disabled={!decEditable || line.qty <= 1}
            onClick={() => {
              // Part 7.1 · fired lines route through a reason dialog.
              // Unfired lines use the shared unfired-dec helper so the
              // PRINTED reopen path stays the single entry.
              if (line.ticket_id != null && onOpenCancelReason) {
                onOpenCancelReason(line);
                return;
              }
              if (onRequestUnfiredDec) onRequestUnfiredDec(line);
            }}
            className="w-11 h-11 rounded border border-[color:var(--pos-color-border,#E2E8DF)] bg-white flex items-center justify-center disabled:opacity-40"
            data-testid={`pos-line-${idx}-dec`}>
            <Minus className="w-3.5 h-3.5" />
          </button>
          <input type="text" inputMode="numeric"
            defaultValue={line.qty}
            key={`qty-${line.id}-${line.qty}`}
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.preventDefault(); e.target.blur(); }
              if (e.key === "Escape") { e.preventDefault(); e.target.value = String(line.qty); e.target.blur(); }
            }}
            onBlur={(e) => {
              const n = parseInt(e.target.value, 10);
              if (!(n > 0) || n === line.qty) { e.target.value = String(line.qty); return; }
              // Sprint 8 · POS-QCS · route through the parent handler
              // so > 999 opens the elevation dialog and > 10,000 hard
              // blocks with a toast. Direct clamp removed intentionally.
              if (onQtyRequest) { onQtyRequest(line, n); return; }
              const nextQty = Math.min(999, n);
              if (livePos) livePos.changeLineQty(orderId, line.id, nextQty);
              else dispatch({ type: ACT.LINE_QTY_CHANGED, payload: { line_id: line.id, qty: nextQty } });
            }}
            disabled={locked}
            className="w-11 h-11 rounded border border-[color:var(--pos-color-border,#E2E8DF)] bg-white text-[13px] font-medium text-[color:var(--pos-color-primary-text,#1A2421)] tabular-nums text-center focus:border-[color:var(--pos-color-accent,var(--color-primary))] focus:outline-none disabled:opacity-60"
            data-testid={`pos-line-${idx}-qty`} />
          <button type="button" disabled={locked}
            onClick={() => {
              const next = Number(line.qty) + 1;
              // Sprint 8 · POS-QCS · route through the parent handler
              // so a tap that would cross 999 opens the elevation
              // dialog instead of dispatching a rejected change.
              if (onQtyRequest) { onQtyRequest(line, next); return; }
              if (livePos) livePos.changeLineQty(orderId, line.id, next);
              else dispatch({ type: ACT.LINE_QTY_CHANGED, payload: { line_id: line.id, qty: next } });
            }}
            className="w-11 h-11 rounded border border-[color:var(--pos-color-border,#E2E8DF)] bg-white flex items-center justify-center disabled:opacity-40"
            data-testid={`pos-line-${idx}-inc`}>
            <Plus className="w-3.5 h-3.5" />
          </button>
        </div>
        <div className="text-[14px] font-semibold text-[color:var(--pos-color-primary-text,#1A2421)] tabular-nums min-w-[4.5rem] text-right"
          data-testid={`pos-line-${idx}-amt`}>
          ₹{lineAmt.toFixed(2)}
        </div>
      </div>

      {popoverOpen && (
        <LineActionPopover line={line} open={popoverOpen}
          onOpenChange={(v) => { if (!v) onClosePopover(); }}
          onOpenNote={onOpenNote}
          onOpenLineDiscount={onOpenLineDiscount}
          onRequestRemove={onRequestRemove}
          onRequestDec={(l) => {
            if (l.ticket_id != null && onOpenCancelReason) {
              onOpenCancelReason(l);
            } else if (onRequestUnfiredDec) {
              onRequestUnfiredDec(l);
            }
          }}
          dispatch={dispatch} locked={locked} orderState={orderState} />
      )}
    </div>
  );
}

function TotalsBlock({ totals, order }) {
  // Financial Hardening (Feb 2026): once the server invoice snapshot
  // is available on the order, the tax lines are rendered verbatim
  // from `totals.tax_lines` (each entry: {regime, rate, taxable_amount,
  // tax_amount}). The local per-component breakdown drives the OPEN /
  // unprinted preview only.
  const serverSnapshot = totals.from_server_snapshot && Array.isArray(totals.tax_lines);
  return (
    <div className="border-t border-[color:var(--pos-color-border,#E2E8DF)] px-3 py-2 bg-[color:var(--pos-color-raised-surface,#F9F9F8)]"
      data-testid="pos-totals-block">
      <Row label="Subtotal" value={totals.subtotal} />
      {totals.discount > 0 && (
        <Row label={`Discount${order.discount ? (order.discount.type === "percent" ? ` (${order.discount.value}%)` : " (flat)") : ""}`}
          value={-totals.discount} testid="pos-total-discount" />
      )}
      {serverSnapshot ? (
        totals.tax_lines.map((tl, i) => {
          const isGst = (tl.regime || "").toUpperCase() === "GST";
          if (isGst) {
            // GST splits equally into CGST + SGST on the same rate.
            const half = Number(tl.tax_amount || 0) / 2;
            return (
              <React.Fragment key={`tl-${i}`}>
                <Row label={`CGST @ ${(tl.rate/2).toFixed(2)}%`} value={half}
                  testid={`pos-total-cgst-${i}`} />
                <Row label={`SGST @ ${(tl.rate/2).toFixed(2)}%`} value={half}
                  testid={`pos-total-sgst-${i}`} />
              </React.Fragment>
            );
          }
          return (
            <Row key={`tl-${i}`} label={`${tl.regime} @ ${tl.rate}%`}
              value={Number(tl.tax_amount || 0)}
              testid={`pos-total-${(tl.regime || "tax").toLowerCase()}-${i}`} />
          );
        })
      ) : (
        <>
          {totals.has_food && (
            <>
              <Row label="CGST @ 2.5%" value={totals.cgst} testid="pos-total-cgst" />
              <Row label="SGST @ 2.5%" value={totals.sgst} testid="pos-total-sgst" />
            </>
          )}
          {totals.has_liquor && (
            <Row label="VAT @ 20%" value={totals.vat} testid="pos-total-vat" />
          )}
        </>
      )}
      {/* Sprint 6 correction 4 · service charge row appears only when
          the outlet has it enabled. Between discount and tax on the
          totals block. */}
      {totals.service_charge_enabled && totals.service_charge > 0 && (
        <Row label="Service charge" value={totals.service_charge}
          testid="pos-total-service-charge" />
      )}
      {Math.abs(totals.round_off) > 0.005 && (
        <Row label="Round off" value={totals.round_off}
          testid="pos-total-round-off" />
      )}
      <div className="flex items-baseline justify-between mt-1 pt-1.5 border-t border-[color:var(--pos-color-border,#E2E8DF)]">
        <span className="text-[11px] uppercase tracking-wider text-[color:var(--pos-color-secondary-text,#5C6B64)] font-medium">Grand total</span>
        <span className="text-[36px] leading-none font-medium text-[color:var(--pos-color-primary-text,#1A2421)] tabular-nums"
          data-testid="pos-grand-total">
          ₹{totals.grand_total.toLocaleString("en-IN", { minimumFractionDigits: 0 })}
        </span>
      </div>
      {order.state === ORDER_STATE.PRINTED && totals.balance > 0 && (
        <div className="mt-1.5 text-[11px] text-[color:var(--pos-color-secondary-text,#5C6B64)] tabular-nums flex justify-between">
          <span>Balance due</span><strong className="text-[color:var(--pos-color-primary-text,#1A2421)]">{fmt(totals.balance)}</strong>
        </div>
      )}
    </div>
  );
}
function Row({ label, value, testid }) {
  return (
    <div className="flex items-center justify-between text-[11px] text-[color:var(--pos-color-secondary-text,#5C6B64)] tabular-nums py-0.5">
      <span>{label}</span>
      <span className="text-[color:var(--pos-color-primary-text,#1A2421)]" data-testid={testid}>{fmt(value)}</span>
    </div>
  );
}

function ActionBtn({ testid, icon: Icon, label, onClick, disabled, primary, alertColor, disabledReason }) {
  // Item 5 (Sprint 4 corrections) · labels never truncate. Buttons
  // widen horizontally to fit; `whitespace-nowrap` prevents wraps.
  // `alertColor="amber"` fills the button amber for consequential
  // actions like "Add items" (reopens a printed bill, voids an
  // invoice number).
  return (
    <button type="button" onClick={onClick} disabled={disabled}
      title={disabled && disabledReason ? disabledReason : undefined}
      data-testid={testid}
      data-disabled-reason={disabled && disabledReason ? disabledReason : undefined}
      className={`min-h-[44px] rounded flex flex-row items-center justify-center gap-1.5 text-[11px] font-medium transition border px-3 whitespace-nowrap
        ${disabled
          ? "border-[color:var(--pos-color-border,#E2E8DF)] bg-[#F4F4F2] text-[#B8B8B5] cursor-not-allowed"
          : alertColor === "amber"
            ? "border-amber-500 bg-amber-500 text-white hover:bg-amber-600 shadow-sm"
          : primary
            ? "border-transparent bg-[color:var(--color-primary)] text-white hover:bg-[color:var(--color-primary-hover)]"
            : "border-[color:var(--pos-color-border,#E2E8DF)] bg-white text-[color:var(--pos-color-primary-text,#1A2421)] hover:bg-[color:var(--pos-color-raised-surface,#F9F9F8)]"}`}>
      <Icon className="w-3.5 h-3.5 shrink-0" />
      <span>{label}</span>
    </button>
  );
}


// ─── Ticket-group helpers · Part 7 ──────────────────────────────────
// Sprint 2 Correction Part 2 · a fired group now also carries the
// human-readable `ticket_no` (int) stamped by the server on every line.
// The group's `ticket_id` (may be a UUID) is only used as a Map key —
// never rendered. Groups with 0 lines are filtered out (an empty
// ticket header was rendering when a fired line was later voided out
// of the client while the UUID group was left behind).
function groupByTicket(lines) {
  const byTicket = new Map();
  const unsent = [];
  for (const l of lines) {
    if (l.voided) continue;
    const tid = l.ticket_id;
    if (tid == null) { unsent.push(l); continue; }
    if (!byTicket.has(tid)) byTicket.set(tid, {
      ticket_id: tid,
      ticket_no: l.ticket_no || null,
      dest: l.ticket_dest || (String(tid).startsWith("BOT") ? "bar" : "kitchen"),
      fired_at: l.ticket_fired_at || 0, lines: [],
    });
    const grp = byTicket.get(tid);
    grp.lines.push(l);
    if (!grp.ticket_no && l.ticket_no) grp.ticket_no = l.ticket_no;
    const t = l.ticket_fired_at;
    if (t && t > grp.fired_at) grp.fired_at = t;
  }
  const fired_groups = Array.from(byTicket.values())
    .filter((g) => g.lines.length > 0)   // drop empty groups
    .sort((a, b) => (a.fired_at - b.fired_at) || String(a.ticket_id).localeCompare(String(b.ticket_id)));
  if (unsent.length > 0) {
    fired_groups.push({ ticket_id: null, ticket_no: null, dest: null, fired_at: null, lines: unsent });
  }
  return fired_groups;
}

function TicketGroupHeader({ group }) {
  if (group.ticket_id == null) {
    return (
      <div className="border-b border-amber-200 bg-amber-100/60 px-3 py-1 flex items-center justify-between"
        data-testid="pos-ticket-header-unsent">
        <span className="text-[10px] uppercase tracking-wider font-semibold text-amber-900">
          Not sent yet · {group.lines.length} line{group.lines.length === 1 ? "" : "s"}
        </span>
        <span className="text-[10px] uppercase tracking-wider text-amber-800">
          Send ticket to fire
        </span>
      </div>
    );
  }
  const t = group.fired_at ? new Date(group.fired_at) : null;
  const isBar = group.dest === "bar";
  // Sprint 2 Correction Part 2 · display label is the human ticket
  // number (KOT_1 / BOT_2), NEVER the underlying UUID. `ticket_no`
  // is stamped on every line by the server; the client also uses
  // KOT_N-style labels locally. The raw group.ticket_id (possibly a
  // hex UUID from the server) is never rendered.
  const displayNo = group.ticket_no
    ? `${isBar ? "BOT" : "KOT"}_${group.ticket_no}`
    : (typeof group.ticket_id === "string" && /^(KOT|BOT)_/.test(group.ticket_id)
        ? group.ticket_id
        : `${isBar ? "BOT" : "KOT"}_?`);
  return (
    <div className={`border-b px-3 py-1 flex items-center justify-between
      ${isBar ? "border-orange-200 bg-orange-50/60" : "border-sky-200 bg-sky-50/60"}`}
      data-testid={`pos-ticket-header-${displayNo}`}>
      <span className={`text-[10px] uppercase tracking-wider font-semibold tabular-nums
        ${isBar ? "text-orange-900" : "text-sky-900"}`}>
        {displayNo} · {isBar ? "BAR" : "KITCHEN"} · {group.lines.length} line{group.lines.length === 1 ? "" : "s"}
      </span>
      {t && (
        <span className={`text-[10px] tabular-nums ${isBar ? "text-orange-800" : "text-sky-800"}`}>
          Fired {t.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
        </span>
      )}
    </div>
  );
}

// ─── Send-KOT confirmation dialog · Sprint 2 Correction Part 1 ─────
// Fires nothing on Cancel — the order is untouched, lines remain
// unfired. Confirm actually fires. Between the two buttons the cashier
// sees an itemised list of what will be sent, grouped by destination.
// The list scrolls; the actions stay pinned to the footer.
function SendKotConfirmDialog({ open, order, lines, stewardName, onCancel, onConfirm }) {
  if (!open) return null;
  const byDest = new Map();
  for (const l of lines) {
    const d = l.routing || "kitchen";
    if (!byDest.has(d)) byDest.set(d, []);
    byDest.get(d).push(l);
  }
  const dests = Array.from(byDest.entries());
  const total = lines.reduce((n, l) => n + (Number(l.qty) || 0), 0);
  // Consolidation-sprint fix (retrospective) · resolve the friendly
  // label BEFORE render. Dine-in fires the KOT to a physical table,
  // so `T1` is what the steward must double-check. `t_g01` would be
  // a raw-identifier leak on the very dialog the audit missed —
  // fixed here alongside a prominence upgrade (Sprint 3B correction).
  const tableLabel = order?.table_id ? resolveTableLabel(order.table_id) : null;
  return (
    <div className="fixed inset-0 z-[80] bg-[#0F1A16]/60 backdrop-blur-sm flex items-center justify-center px-6"
         data-testid="pos-kot-confirm">
      <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl flex flex-col max-h-[85vh]">
        <div className="px-5 py-4 border-b border-[color:var(--pos-color-border,#E2E8DF)]">
          <div className="text-[10px] uppercase tracking-widest text-[color:var(--pos-color-secondary-text,#5C6B64)] font-semibold">Send ticket</div>
          {/* Table (or Token / Room) is the FIRST thing the steward
              reads — a glance-level check that they are firing to the
              right seat. Rendered at hero-size, distinct chip, so it
              dominates the dialog after the primary action button. */}
          {tableLabel && (
            <div className="mt-2 flex items-baseline gap-2" data-testid="pos-kot-confirm-table">
              <span className="text-[11px] uppercase tracking-widest text-[color:var(--pos-color-secondary-text,#5C6B64)]">Table</span>
              <span className="text-[36px] leading-none font-semibold text-[color:var(--pos-color-primary-text,#1A2421)] tabular-nums">
                {tableLabel}
              </span>
            </div>
          )}
          {!tableLabel && order?.token && (
            <div className="mt-2 flex items-baseline gap-2" data-testid="pos-kot-confirm-token">
              <span className="text-[11px] uppercase tracking-widest text-[color:var(--pos-color-secondary-text,#5C6B64)]">Token</span>
              <span className="text-[36px] leading-none font-semibold text-[color:var(--pos-color-primary-text,#1A2421)] tabular-nums">
                {order.token}
              </span>
            </div>
          )}
          <h2 className="mt-3 text-[15px] font-semibold text-[color:var(--pos-color-primary-text,#1A2421)] leading-tight">
            {`Confirm ${total} item${total === 1 ? "" : "s"} to fire`}
          </h2>
          <div className="mt-1 text-[11px] text-[color:var(--pos-color-secondary-text,#5C6B64)] flex flex-wrap gap-x-3 gap-y-0.5" data-testid="pos-kot-confirm-header">
            <span>{order?.type || "DINE-IN"}</span>
            {order?.covers != null && <span>· {order.covers} pax</span>}
            {stewardName && <span>· Steward <strong className="text-[color:var(--pos-color-primary-text,#1A2421)]">{stewardName}</strong></span>}
          </div>
        </div>
        <div className="px-5 py-2 overflow-y-auto flex-1" data-testid="pos-kot-confirm-list">
          {dests.length === 0 && (
            <div className="text-[12px] text-[color:var(--pos-color-secondary-text,#5C6B64)] py-6 text-center">No unfired items on this order.</div>
          )}
          {dests.map(([d, dl]) => (
            <div key={d} className="mt-2">
              <div className={`text-[10px] uppercase tracking-widest font-semibold mb-1 px-2 py-1 rounded
                ${d === "bar" ? "text-orange-900 bg-orange-100" : "text-sky-900 bg-sky-100"}`}
                data-testid={`pos-kot-confirm-dest-${d}`}>
                {d === "bar" ? "Bar" : "Kitchen"} · {dl.length} line{dl.length === 1 ? "" : "s"}
              </div>
              <div className="divide-y divide-[color:var(--pos-color-border,#EEF1EE)]">
                {dl.map((l, i) => (
                  <div key={l.id} className="py-1.5 flex items-start gap-3 text-[12px]"
                       data-testid={`pos-kot-confirm-line-${i}`}>
                    <div className="w-8 text-right font-semibold text-[color:var(--pos-color-primary-text,#1A2421)] tabular-nums">{l.qty}</div>
                    <div className="flex-1">
                      <div className="text-[color:var(--pos-color-primary-text,#1A2421)] font-medium">{l.item_name}</div>
                      {l.note && (
                        <div className="text-rose-700 text-[11px] font-semibold uppercase tracking-wide mt-0.5"
                             data-testid={`pos-kot-confirm-line-${i}-note`}>
                          ** {l.note} **
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="px-5 py-3 border-t border-[color:var(--pos-color-border,#E2E8DF)] flex items-center justify-end gap-2 shrink-0">
          <button type="button" onClick={onCancel}
            data-testid="pos-kot-confirm-cancel"
            className="h-11 px-4 rounded text-[13px] font-medium text-[color:var(--pos-color-secondary-text,#5C6B64)] hover:bg-[color:var(--pos-color-page-bg,#F4F6F2)]">
            Cancel
          </button>
          <button type="button" onClick={onConfirm} disabled={lines.length === 0}
            data-testid="pos-kot-confirm-fire"
            className="h-11 px-5 rounded bg-[color:var(--color-primary)] text-white text-[13px] font-semibold hover:brightness-110 disabled:opacity-40">
            Confirm & fire
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Kitchen-note dialog · Task 1c ──────────────────────────────────
function KitchenNoteDialog({ line, onCancel, onSave }) {  const [note, setNote] = useState("");
  useEffect(() => {
    if (line) setNote(line.note || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [line?.id]);
  if (!line) return null;
  const readOnly = line.ticket_id != null;

  const currentParts = () => note.split(/[,;]\s*/).map((s) => s.trim()).filter(Boolean);
  const isOn = (p) => currentParts().some((x) => x.toLowerCase() === p.toLowerCase());
  const togglePreset = (p) => {
    const parts = currentParts();
    const idx = parts.findIndex((x) => x.toLowerCase() === p.toLowerCase());
    if (idx >= 0) parts.splice(idx, 1); else parts.push(p);
    setNote(parts.join(", "));
  };

  return (
    <Dialog open={!!line} onOpenChange={(v) => { if (!v) onCancel(); }}>
      <DialogContent className="max-w-[540px] max-h-[420px] p-0 gap-0 border border-[color:var(--pos-color-border,#E2E8DF)] overflow-hidden flex flex-col"
        data-testid="pos-note-dialog">
        <div className="px-5 py-3 border-b border-[color:var(--pos-color-border,#E2E8DF)]">
          <div className="text-[10px] uppercase tracking-wider text-[color:var(--pos-color-secondary-text,#5C6B64)]">Kitchen note</div>
          <div className="text-[15px] font-semibold text-[color:var(--pos-color-primary-text,#1A2421)]">{line.item_name}</div>
          {readOnly && (
            <div className="text-[11px] text-[color:var(--pos-color-secondary-text,#5C6B64)] mt-1 italic"
              data-testid="pos-note-readonly-reason">
              Read-only — {line.ticket_id} has already been fired to the kitchen. Notes cannot be changed once the ticket is printed.
            </div>
          )}
        </div>

        <div className="p-4 space-y-3">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[color:var(--pos-color-secondary-text,#5C6B64)] mb-1.5">
              Quick presets · tap to toggle
            </div>
            <div className="flex flex-wrap gap-1.5" data-testid="pos-note-presets">
              {NOTE_PRESETS.map((p) => {
                const on = isOn(p);
                return (
                  <button key={p} type="button" disabled={readOnly}
                    onClick={() => togglePreset(p)}
                    data-testid={`pos-note-preset-${p.replace(/\s+/g, "-").toLowerCase()}`}
                    className={`min-h-[36px] px-3 rounded-full border text-[12px] font-medium transition
                      ${readOnly ? "opacity-50 cursor-not-allowed border-[color:var(--pos-color-border,#E2E8DF)] bg-white text-[color:var(--pos-color-secondary-text,#5C6B64)]"
                        : on ? "border-[color:var(--color-primary)] bg-[color:var(--color-primary)] text-white"
                             : "border-[color:var(--pos-color-border,#E2E8DF)] bg-white text-[color:var(--pos-color-primary-text,#1A2421)] hover:bg-[color:var(--pos-color-raised-surface,#F9F9F8)]"}`}>
                    {p}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[color:var(--pos-color-secondary-text,#5C6B64)] mb-1.5">Free text</div>
            <textarea value={note} onChange={(e) => setNote(e.target.value)}
              disabled={readOnly} rows={3}
              className="w-full rounded border border-[color:var(--pos-color-border,#E2E8DF)] p-2 text-[13px] text-[color:var(--pos-color-primary-text,#1A2421)] focus:border-[color:var(--pos-color-accent,var(--color-primary))] focus:outline-none disabled:bg-[color:var(--pos-color-raised-surface,#F9F9F8)] disabled:text-[color:var(--pos-color-secondary-text,#5C6B64)]"
              placeholder="e.g. Very well done, no coriander garnish"
              data-testid="pos-note-textarea" />
          </div>
        </div>

        <div className="p-3 border-t border-[color:var(--pos-color-border,#E2E8DF)] flex items-center justify-end gap-2">
          <button type="button" onClick={onCancel}
            data-testid="pos-note-cancel"
            className="min-h-[44px] px-4 rounded text-[12px] font-medium text-[color:var(--pos-color-secondary-text,#5C6B64)] hover:bg-[color:var(--pos-color-page-bg,#F4F6F2)] transition">
            {readOnly ? "Close" : "Cancel"}
          </button>
          {!readOnly && (
            <button type="button" onClick={() => onSave(note)}
              data-testid="pos-note-save"
              className="min-h-[44px] px-5 rounded bg-[color:var(--color-primary)] text-white text-[13px] font-semibold hover:bg-[color:var(--color-primary-hover)] transition">
              Save note
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Held-orders tray reachable from billing top bar ─────────────────
function BillingHeldTray({ held, onClose, onResume }) {
  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);
  return (
    <Dialog open={true} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md w-[92vw] max-h-[92vh] p-0 gap-0 border border-[color:var(--pos-color-border,#E2E8DF)] flex flex-col overflow-hidden"
        data-testid="pos-held-tray">
        <div className="px-4 py-3 border-b border-[color:var(--pos-color-border,#E2E8DF)] flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[color:var(--pos-color-secondary-text,#5C6B64)]">Held orders</div>
            <div className="text-[15px] font-semibold text-[color:var(--pos-color-primary-text,#1A2421)]">{held.length} on hold</div>
          </div>
          <button type="button" onClick={onClose}
            className="h-8 px-2.5 rounded border border-[color:var(--pos-color-border,#E2E8DF)] text-[11px]"
            data-testid="pos-held-tray-close">Close · Esc</button>
        </div>
        <div className="flex-1 overflow-y-auto p-3 space-y-2">
          {held.length === 0 && (
            <div className="text-center text-[12px] text-[color:var(--pos-color-secondary-text,#5C6B64)] py-8">No orders on hold.</div>
          )}
          {held.map((o) => {
            const total = (o.lines || []).reduce((s, l) => s + l.qty * l.unit_price, 0);
            const held_min = o.order.held_at ? Math.max(0, Math.floor((Date.now() - o.order.held_at) / 60000)) : 0;
            const identifier = o.order.type === ORDER_TYPE.DINE_IN
              ? (TABLES.find((t) => t.id === o.order.table_id)?.label || "—")
              : (o.order.token || "—");
            return (
              <button key={o.order.id} type="button" onClick={() => onResume(o.order.id)}
                data-testid={`pos-held-entry-${o.order.id}`}
                className="w-full text-left rounded border border-[color:var(--pos-color-border,#E2E8DF)] bg-white hover:bg-[color:var(--pos-color-raised-surface,#F9F9F8)] p-3 transition">
                <div className="flex items-center justify-between">
                  <div className="text-[10px] uppercase tracking-wider text-sky-800 font-medium">
                    {o.order.type.replace("_", " ")}
                  </div>
                  <div className="text-[10px] text-[color:var(--pos-color-secondary-text,#5C6B64)] tabular-nums">Held {held_min}m ago</div>
                </div>
                <div className="mt-1 flex items-center justify-between">
                  <div className="text-[15px] font-semibold text-[color:var(--pos-color-primary-text,#1A2421)] tabular-nums">{identifier}</div>
                  <div className="text-[14px] font-semibold text-[color:var(--pos-color-primary-text,#1A2421)] tabular-nums">
                    ₹{Math.round(total).toLocaleString("en-IN")}
                  </div>
                </div>
                <div className="mt-0.5 text-[11px] text-[color:var(--pos-color-secondary-text,#5C6B64)] tabular-nums">
                  {o.lines.length} line{o.lines.length === 1 ? "" : "s"} · tap to resume
                </div>
              </button>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── (Split placeholder deleted · Part 1.3) ─────────────────────────
// Prior sprints had a workspace-class placeholder here just to satisfy
// screenshot ergonomics. Replaced by the real SplitDialog in
// Sprint2Dialogs.js (Part 6). No artefacts remain.

// ─── SplitChildrenView · Part 3.2 & 3.3 ─────────────────────────────
// Shows every child of a split parent side-by-side with its own
// state (unprinted / printed / settled), balance, and its own Print,
// Send, and Pay controls. Prints allocate independent invoice numbers.
// Payments open the PaymentOverlay for the chosen child; on completion
// the split view re-opens so the cashier never loses sight of the
// remaining siblings.
function SplitChildrenView({ open, state, parentId, onClose, onPrintChild, onReprintChild, onPayChild, onOpenPreview }) {
  if (!open || !parentId) return null;
  const parent = state.orders?.[parentId];
  const children = childrenOf(state, parentId);
  // POS Control Sprint · header and card labels ALWAYS derive from
  // the actual children in state, never from the originally-requested
  // split N. If the server collapses an empty group or two, the
  // dialog stays consistent ("2 child bills" ↔ "Split 1 of 2 · Split
  // 2 of 2"; "3 child bills" ↔ "Split 1 of 3 · Split 2 of 3 · Split
  // 3 of 3").
  const actualSplitCount = children.length;
  const anyOutstanding = children.some((c) =>
    c.order.state !== ORDER_STATE.SETTLED && c.order.state !== ORDER_STATE.VOIDED
  );
  // Compact consolidated layout — side-by-side at 3+ children.
  const gridCls = actualSplitCount >= 3
    ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
    : "grid-cols-1 sm:grid-cols-2";
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-[1100px] w-[95vw] max-h-[92vh] p-0 gap-0 border border-purple-200 overflow-hidden flex flex-col"
        data-testid="pos-split-children-view">
        <DialogHeader className="px-5 py-3 border-b border-purple-100 bg-purple-50 space-y-0 flex-row items-center justify-between">
          <div>
            <DialogTitle className="text-[10px] uppercase tracking-wider text-purple-900 font-normal">Split bill</DialogTitle>
            <DialogDescription className="text-[15px] font-semibold text-[color:var(--pos-color-primary-text,#1A2421)]"
              data-testid="pos-split-view-header-count">
              {actualSplitCount} child bill{actualSplitCount === 1 ? "" : "s"}
              {anyOutstanding && <span className="ml-2 text-[11px] text-purple-900 font-normal">
                · table stays BILLED until every child settles
              </span>}
            </DialogDescription>
          </div>
          <button type="button" onClick={onClose}
            data-testid="pos-split-view-close"
            className="h-8 px-3 rounded border border-purple-200 text-[11px] text-purple-900 hover:bg-purple-100">
            Close
          </button>
        </DialogHeader>

        <div className={`flex-1 overflow-y-auto p-3 grid ${gridCls} gap-3`}>
          {children.map((c, idx) => (
            <SplitChildCard key={c.order.id}
              slice={{ ...c, order: { ...c.order, split_label: `Split ${idx + 1} of ${actualSplitCount}` } }}
              totals={computeTotalsFor(state, c.order.id)}
              onPrint={() => onPrintChild(c.order.id)}
              onReprint={() => onReprintChild && onReprintChild(c.order.id)}
              onSend={() => onOpenPreview(c.order.id)}
              onPay={() => onPayChild(c.order.id)} />
          ))}
        </div>

        <div className="px-4 py-2 border-t border-[color:var(--pos-color-border,#E2E8DF)] text-[10px] text-[color:var(--pos-color-secondary-text,#5C6B64)] flex items-center justify-between">
          <span data-testid="pos-split-view-parent-inv">
            Parent invoice · {parent?.order?.invoice_no || "—"}
            {parent?.order?.voided_invoice && (
              <span className="ml-2 text-amber-800">was <strong>{parent.order.voided_invoice}</strong> · voided</span>
            )}
          </span>
          <span>
            {children.filter((c) => c.order.state === ORDER_STATE.SETTLED).length} of {children.length} settled
          </span>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SplitChildCard({ slice, totals, onPrint, onReprint, onSend, onPay }) {
  const o = slice.order;
  const state = o.state;
  const isSettled  = state === ORDER_STATE.SETTLED;
  const isPrinted  = state === ORDER_STATE.PRINTED;
  const stateLabel = isSettled ? "SETTLED" : isPrinted ? "PRINTED" : "OPEN";
  const stateCls   = isSettled  ? "bg-emerald-100 text-emerald-800"
                   : isPrinted  ? "bg-purple-100 text-purple-800"
                   : "bg-amber-100 text-amber-800";
  const balance = Math.max(0, totals.grand_total - totals.paid);
  return (
    // POS Control Sprint · compact split card. Renders on the same
    // consolidated screen next to its siblings; carries only the fields
    // an operator needs at a glance: split label, gross payable
    // (grand_total includes tax + service charge via the canonical
    // server snapshot), invoice number once printed, current state
    // and per-state financial actions. Reprint is available on both
    // PRINTED and SETTLED children; PRINTED children also allow
    // Settle. Never duplicates tax/payable maths — always reads
    // through computeTotalsFor / server_totals via the parent view.
    <div className={`rounded-lg border p-3 flex flex-col ${isSettled ? "border-emerald-200 bg-emerald-50/40" : isPrinted ? "border-purple-200 bg-white" : "border-[color:var(--pos-color-border,#E2E8DF)] bg-white"}`}
      data-testid={`pos-split-child-${o.id}`}>
      {/* Header · label + state chip */}
      <div className="flex items-center justify-between">
        <div className="text-[13px] font-semibold text-[color:var(--pos-color-primary-text,#1A2421)]"
          data-testid={`pos-split-child-${o.id}-label`}>
          {o.split_label || "Child"}
        </div>
        <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ${stateCls}`}
          data-testid={`pos-split-child-${o.id}-state`}>{stateLabel}</span>
      </div>

      {/* Gross payable (INCLUDING TAX) · single hero number */}
      <div className="mt-2 text-[22px] font-semibold tabular-nums text-[color:var(--pos-color-primary-text,#1A2421)]"
        data-testid={`pos-split-child-${o.id}-total`}>
        ₹{fmt2(totals.grand_total)}
      </div>
      <div className="text-[10px] text-[color:var(--pos-color-secondary-text,#5C6B64)] tabular-nums"
        data-testid={`pos-split-child-${o.id}-invoice`}>
        Invoice · {o.invoice_no || "— to be allocated"}
      </div>
      {balance > 0 && isPrinted && (
        <div className="text-[10px] text-rose-800 tabular-nums mt-0.5"
          data-testid={`pos-split-child-${o.id}-balance`}>
          Balance ₹{fmt2(balance)}
        </div>
      )}

      {/* Actions · per-state */}
      <div className="mt-3 flex items-center gap-1.5">
        {!isPrinted && !isSettled && (
          <button type="button" onClick={onPrint}
            disabled={slice.lines.length === 0}
            data-testid={`pos-split-child-${o.id}-print`}
            className="flex-1 h-9 rounded bg-[color:var(--color-primary)] text-white text-[11px] font-semibold inline-flex items-center justify-center gap-1 hover:bg-[color:var(--color-primary-hover)] disabled:opacity-40">
            <Printer className="w-3 h-3" /> Print bill
          </button>
        )}
        {isPrinted && !isSettled && (
          <>
            <button type="button" onClick={onReprint}
              data-testid={`pos-split-child-${o.id}-reprint`}
              className="h-9 px-2.5 rounded border border-[color:var(--pos-color-border,#E2E8DF)] text-[11px] font-medium text-[color:var(--pos-color-primary-text,#1A2421)] inline-flex items-center justify-center gap-1 hover:bg-[color:var(--pos-color-raised-surface,#F9F9F8)]">
              <Printer className="w-3 h-3" /> Reprint
            </button>
            <button type="button" onClick={onPay}
              data-testid={`pos-split-child-${o.id}-pay`}
              className="flex-1 h-9 rounded bg-emerald-700 text-white text-[11px] font-semibold inline-flex items-center justify-center gap-1 hover:bg-emerald-800">
              <Check className="w-3 h-3" /> Settle
            </button>
          </>
        )}
        {isSettled && (
          <>
            <button type="button" onClick={onReprint}
              data-testid={`pos-split-child-${o.id}-reprint-settled`}
              className="h-9 px-2.5 rounded border border-[color:var(--pos-color-border,#E2E8DF)] text-[11px] font-medium text-[color:var(--pos-color-primary-text,#1A2421)] inline-flex items-center justify-center gap-1 hover:bg-[color:var(--pos-color-raised-surface,#F9F9F8)]">
              <Printer className="w-3 h-3" /> Reprint
            </button>
            <div className="flex-1 h-9 rounded bg-emerald-100 text-emerald-800 text-[11px] font-semibold flex items-center justify-center"
              data-testid={`pos-split-child-${o.id}-settled-marker`}>
              <Check className="w-3 h-3 mr-1" /> Settled
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function fmt2(n) { return Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }

// ─── ReprintReasonDialog · POS Control Sprint ─────────────────────
// Predefined reason chips + free-text notes. Free text is required
// when "Other" is selected. Confirming this dialog kicks off the
// Manager-PIN flow at the call site (SharedPinDialog); the reason
// and notes flow into `livePos.reprintBill` as the audit payload.
const REPRINT_REASONS = [
  { code: "GUEST_COPY",        label: "Guest requested copy" },
  { code: "PRINTER_FAILURE",   label: "Printer failure / paper jam" },
  { code: "LOST_OR_DAMAGED",   label: "Original lost or damaged" },
  { code: "ACCOUNTS_COPY",     label: "Accounts / office copy" },
  { code: "OTHER",             label: "Other" },
];
function ReprintReasonDialog({ open, invoiceNo, onCancel, onConfirm }) {
  const [reasonCode, setReasonCode] = useState(null);
  const [notes, setNotes] = useState("");
  useEffect(() => { if (open) { setReasonCode(null); setNotes(""); } }, [open]);
  const isOther = reasonCode === "OTHER";
  const valid = !!reasonCode && (!isOther || notes.trim().length > 0);
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onCancel(); }}>
      <DialogContent className="max-w-md p-0 gap-0 border border-purple-200 overflow-hidden"
        data-testid="pos-reprint-reason-dialog">
        <DialogHeader className="px-5 py-3 border-b border-purple-100 bg-purple-50 space-y-0">
          <DialogTitle className="text-[10px] uppercase tracking-wider text-purple-900 font-normal">Reprint invoice</DialogTitle>
          <DialogDescription className="text-[13px] font-semibold text-[color:var(--pos-color-primary-text,#1A2421)]">
            {invoiceNo || "—"} · same invoice number will be reissued with a REPRINT watermark
          </DialogDescription>
        </DialogHeader>
        <div className="p-4 space-y-3">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[color:var(--pos-color-secondary-text,#5C6B64)] mb-1.5">
              Reason (required)
            </div>
            <div className="grid grid-cols-1 gap-1.5">
              {REPRINT_REASONS.map((r) => (
                <button key={r.code} type="button" onClick={() => setReasonCode(r.code)}
                  data-testid={`pos-reprint-reason-${r.code.toLowerCase()}`}
                  className={`h-9 rounded border text-[12px] text-left px-3 transition
                    ${reasonCode === r.code
                      ? "border-purple-500 bg-purple-50 text-purple-900 font-semibold"
                      : "border-[color:var(--pos-color-border,#E2E8DF)] bg-white text-[color:var(--pos-color-primary-text,#1A2421)] hover:bg-[color:var(--pos-color-raised-surface,#F9F9F8)]"}`}>
                  {r.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[color:var(--pos-color-secondary-text,#5C6B64)]">
              Notes {isOther && <span className="text-rose-700">(required for “Other”)</span>}
            </label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Optional context for the audit trail"
              data-testid="pos-reprint-notes"
              className="w-full rounded border border-[color:var(--pos-color-border,#E2E8DF)] bg-white p-2 text-[12px] focus:outline-none focus:border-slate-500" />
          </div>
        </div>
        <div className="px-4 py-2 border-t border-[color:var(--pos-color-border,#E2E8DF)] flex items-center justify-end gap-2 bg-[color:var(--pos-color-raised-surface,#F9F9F8)]">
          <button type="button" onClick={onCancel}
            data-testid="pos-reprint-reason-cancel"
            className="h-9 px-3 rounded text-[12px] font-medium text-[color:var(--pos-color-secondary-text,#5C6B64)] hover:bg-[color:var(--pos-color-card-bg,#FFFFFF)]">
            Cancel
          </button>
          <button type="button"
            disabled={!valid}
            onClick={() => onConfirm({ reason_code: reasonCode, notes: notes.trim() })}
            data-testid="pos-reprint-reason-confirm"
            className="h-9 px-4 rounded bg-purple-700 text-white text-[12px] font-semibold hover:bg-purple-800 disabled:opacity-40 disabled:cursor-not-allowed">
            Continue · Manager PIN →
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Cancellation reason dialog · Part 7.1 ──────────────────────────
// Every reason capture in the terminal now behaves identically:
// preset chips + optional free-text note, wired through the shared
// ReasonInput. Void was already done in an earlier sprint; this
// completes cancellation of a fired ticket line.
function CancellationReasonDialog({ line, onCancel, onConfirm }) {
  const [reason, setReason] = useReasonState();
  useEffect(() => { if (line) setReason({ reason_code: null, note: "" }); }, [line?.id, setReason]);
  if (!line) return null;
  const submit = () => {
    if (!reasonValid(reason)) return;
    const n = normaliseReason(reason);
    onConfirm({ reason_code: n.reason_code, note: n.note });
  };
  return (
    <Dialog open={!!line} onOpenChange={(v) => { if (!v) onCancel(); }}>
      <DialogContent className="max-w-md p-0 gap-0 border border-rose-200 overflow-hidden"
        data-testid="pos-cancel-reason-dialog">
        <DialogHeader className="px-5 py-3 border-b border-rose-100 bg-rose-50 space-y-0">
          <DialogTitle className="text-[10px] uppercase tracking-wider text-rose-900 font-normal">Cancel from ticket</DialogTitle>
          <DialogDescription className="text-[13px] font-semibold text-[color:var(--pos-color-primary-text,#1A2421)]">
            {line.item_name} × {line.qty} · fired as <span className="tabular-nums">{line.ticket_id}</span>
          </DialogDescription>
        </DialogHeader>
        <div className="p-4 space-y-3">
          <ReasonInput
            presets={CANCEL_PRESETS}
            value={reason}
            onChange={setReason}
            testidPrefix="pos-cancel" />
          <div className="text-[11px] text-[color:var(--pos-color-secondary-text,#5C6B64)] italic">
            Authorised by <strong className="text-[color:var(--pos-color-primary-text,#1A2421)] tabular-nums">system</strong>
          </div>
        </div>
        <div className="p-3 border-t border-[color:var(--pos-color-border,#E2E8DF)] flex items-center gap-2 justify-end">
          <button type="button" onClick={onCancel}
            data-testid="pos-cancel-reason-cancel"
            className="min-h-[44px] px-4 rounded text-[12px] font-medium text-[color:var(--pos-color-secondary-text,#5C6B64)] hover:bg-[color:var(--pos-color-page-bg,#F4F6F2)]">
            Keep it
          </button>
          <button type="button" onClick={submit}
            disabled={!reasonValid(reason)}
            data-testid="pos-cancel-reason-confirm"
            className="min-h-[44px] px-4 rounded bg-rose-700 text-white text-[13px] font-medium hover:bg-rose-800 disabled:opacity-40">
            Cancel from ticket
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Guest details dialog · Part 5.1 + 5.2 ──────────────────────────
// Phone FIRST. On a 10-digit number we run a mock lookup and pre-fill
// name (and, for delivery, offer stored addresses). Address is
// REQUIRED for delivery orders. A consent line is always shown and a
// timestamp attached to the order on save.
function GuestDetailsDialog({ open, order, onCancel, onSave }) {
  const type = order?.type;
  const isDelivery = type === ORDER_TYPE.DELIVERY;
  const bank = useCustomerBank();
  const [phone,   setPhone]   = useState("");
  const [name,    setName]    = useState("");
  const [address, setAddress] = useState("");
  const [addressCustom, setAddressCustom] = useState(false);
  const [prefill, setPrefill] = useState(null);
  const [consent, setConsent] = useState(true);

  useEffect(() => {
    if (!open) return;
    setPhone(order?.guest_phone || "");
    setName(order?.guest_name  || "");
    setAddress(order?.guest_address || "");
    setAddressCustom(false);
    setPrefill(null);
    setConsent(true);
  }, [open, order?.id]);

  useEffect(() => {
    const digits = normalisePhone(phone);
    if (digits.length !== 10) { setPrefill(null); return; }
    // POS Control Sprint · consolidated guest lookup via useCustomerBank
    // — the phone→profile hydration behaviour is unchanged, but the
    // catalogue is now behind a single hook so the eventual live
    // customer-bank endpoint is a one-line swap.
    const g = bank.lookup(digits);
    if (g) {
      setPrefill(g);
      setName((v) => v ? v : g.name);
      if (isDelivery && !address && g.addresses?.length) {
        setAddress(g.addresses[0].line);
      }
    } else {
      setPrefill(null);
    }
  }, [phone, isDelivery, bank]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open) return null;
  const canSave = phone.trim().length >= 4 && (!isDelivery || address.trim().length > 0);
  const submit = () => {
    if (!canSave) return;
    // POS Control Sprint · route the eventual customer upsert through
    // the same hook so the live implementation reaches every capture
    // site without repeat code. In mock mode this is a no-op; the
    // per-order guest fields still flow to the reducer as they did.
    bank.upsert({
      phone:   normalisePhone(phone),
      name:    name.trim() || null,
      address: isDelivery ? address.trim() : (address.trim() || null),
      tags:    isDelivery ? ["delivery"] : ["takeaway"],
    });
    onSave({
      guest_phone:   normalisePhone(phone),
      guest_name:    name.trim() || null,
      guest_address: isDelivery ? address.trim() : (address.trim() || null),
      consent_captured_at: consent ? Date.now() : null,
    });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onCancel(); }}>
      <DialogContent className="max-w-[600px] max-h-[92vh] p-0 gap-0 border border-[color:var(--pos-color-border,#E2E8DF)] overflow-hidden flex flex-col"
        data-testid="pos-guest-dialog">
        <DialogHeader className="px-4 py-3 border-b border-[color:var(--pos-color-border,#E2E8DF)] space-y-0">
          <DialogTitle className="text-[10px] uppercase tracking-wider text-[color:var(--pos-color-secondary-text,#5C6B64)] font-normal">Guest details</DialogTitle>
          <DialogDescription className="text-[14px] font-semibold text-[color:var(--pos-color-primary-text,#1A2421)]">
            {isDelivery ? "Delivery — phone, name and address required"
                        : type === ORDER_TYPE.ROOM_SERVICE ? "Room service — phone identifies the guest"
                        : "Takeaway — phone identifies the guest"}
          </DialogDescription>
        </DialogHeader>

        <div className="p-4 space-y-3 overflow-y-auto">
          {/* Phone — always first */}
          <div>
            <label className="text-[9px] uppercase tracking-widest text-[color:var(--pos-color-secondary-text,#5C6B64)]">Phone · lookup key</label>
            <input type="tel" value={phone} autoFocus
              onChange={(e) => setPhone(e.target.value.replace(/[^0-9+ ]/g, ""))}
              inputMode="numeric"
              placeholder="10-digit phone (try 9820011122)"
              className={`w-full h-11 rounded border text-[15px] tabular-nums px-3 focus:outline-none
                ${prefill ? "border-emerald-500 bg-emerald-50" : "border-[color:var(--pos-color-border,#E2E8DF)] bg-white focus:border-[color:var(--pos-color-accent,var(--color-primary))]"}`}
              data-testid="pos-guest-phone" />
            {prefill && (
              <div className="text-[10px] text-emerald-800 mt-0.5" data-testid="pos-guest-prefill">
                Returning guest — name pre-filled{isDelivery && prefill.addresses?.length ? `, ${prefill.addresses.length} address${prefill.addresses.length === 1 ? "" : "es"} on file` : ""}.
              </div>
            )}
          </div>

          {/* Name — pre-filled if lookup hit, still editable */}
          <div>
            <label className="text-[9px] uppercase tracking-widest text-[color:var(--pos-color-secondary-text,#5C6B64)]">Name</label>
            <input type="text" value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Guest name"
              className="w-full h-10 rounded border border-[color:var(--pos-color-border,#E2E8DF)] text-[13px] px-2 focus:border-[color:var(--pos-color-accent,var(--color-primary))] focus:outline-none"
              data-testid="pos-guest-name" />
          </div>

          {/* Delivery-only address section */}
          {isDelivery && (
            <div data-testid="pos-guest-address-block">
              <label className="text-[9px] uppercase tracking-widest text-[color:var(--pos-color-secondary-text,#5C6B64)]">Delivery address · required</label>
              {prefill?.addresses?.length > 0 && !addressCustom && (
                <div className="space-y-1 mb-1">
                  {prefill.addresses.map((a) => (
                    <button type="button" key={a.id}
                      onClick={() => setAddress(a.line)}
                      data-testid={`pos-guest-address-preset-${a.id}`}
                      className={`w-full text-left rounded border p-2 text-[11px] transition
                        ${address === a.line
                          ? "border-emerald-500 bg-emerald-50"
                          : "border-[color:var(--pos-color-border,#E2E8DF)] bg-white hover:bg-[color:var(--pos-color-raised-surface,#F9F9F8)]"}`}>
                      <div className="text-[10px] uppercase tracking-wider text-[color:var(--pos-color-secondary-text,#5C6B64)]">{a.label}</div>
                      <div className="text-[12px] text-[color:var(--pos-color-primary-text,#1A2421)]">{a.line}</div>
                    </button>
                  ))}
                  <button type="button"
                    onClick={() => { setAddressCustom(true); setAddress(""); }}
                    data-testid="pos-guest-address-custom-open"
                    className="text-[11px] text-sky-800 hover:underline mt-1">
                    + Use a different address
                  </button>
                </div>
              )}
              <textarea rows={2} value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="House / flat, street, area, city, PIN"
                className="w-full rounded border border-[color:var(--pos-color-border,#E2E8DF)] text-[12px] px-2 py-1.5 focus:border-[color:var(--pos-color-accent,var(--color-primary))] focus:outline-none"
                data-testid="pos-guest-address" />
            </div>
          )}

          {/* Part 5.3 · consent line */}
          <label className="flex items-start gap-2 text-[11px] text-[color:var(--pos-color-secondary-text,#5C6B64)] cursor-pointer select-none"
            data-testid="pos-guest-consent">
            <input type="checkbox" checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              data-testid="pos-guest-consent-checkbox"
              className="mt-0.5 accent-[color:var(--color-primary)]" />
            <span>Guest agrees these details are stored for order and service purposes.</span>
          </label>
        </div>

        <div className="p-3 border-t border-[color:var(--pos-color-border,#E2E8DF)] flex items-center justify-end gap-2">
          <button type="button" onClick={onCancel}
            data-testid="pos-guest-cancel"
            className="h-10 px-3 rounded text-[12px] font-medium text-[color:var(--pos-color-secondary-text,#5C6B64)] hover:bg-[color:var(--pos-color-page-bg,#F4F6F2)]">
            Cancel
          </button>
          <button type="button" onClick={submit}
            disabled={!canSave}
            data-testid="pos-guest-save"
            className="h-10 px-4 rounded bg-[color:var(--color-primary)] text-white text-[12px] font-semibold hover:bg-[color:var(--color-primary-hover)] disabled:opacity-40">
            Save details
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

